import React, { useRef, useCallback } from 'react';
import './App.css';
import Header from './components/Header/Header';
import GlobalOptions from './components/GlobalOptions/GlobalOptions';
import GeneratedConfig from './components/GeneratedConfig/GeneratedConfig';
import BasicInformation from './components/DBConfigurations/PatchSection/BasicInformation';
import PatchContent, { PatchOptionsState } from './components/DBConfigurations/PatchSection/PatchContent';
import DownloadSettings from './components/DBConfigurations/PatchSection/DownloadSettings';
import AdditionalOptions from './components/DBConfigurations/PatchSection/AdditionalOptions';
import HomeSettings from './components/DBConfigurations/PatchSection/HomeSettings';
import ReferencesURL from './components/ReferencesURL';
import { Box, Typography, CssBaseline, Paper, Tabs, Tab, Divider, Select, MenuItem, IconButton, Tooltip } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import Footer from './components/Footer';
import UpgradeSection from './components/DBConfigurations/UpgradeSection/UpgradeSection';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import Changelog from './components/Changelog';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LoadConfigFile from './components/LoadConfigFile/LoadConfigFile';
import ValidationMessages from './components/GeneratedConfig/ValidationMessages';
import { getPatchValidationMessages } from './components/DBConfigurations/PatchSection/BasicInformation';
import AutoUpgradeReleaseCard from './components/AutoUpgradeReleaseCard';
import { getTargetVersion, getSourceVersion, loadVersionConfig, DEFAULT_VERSION_CONFIG, VersionConfig } from './versionConfig';

const getDesignTokens = (mode: 'light' | 'dark') => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          background: {
            default: '#f4f6fa',
            paper: '#fff',
          },
          primary: { main: '#003366' },
          secondary: { main: '#1976d2' },
          accent: { main: '#003366' },
        }
      : {
          background: {
            default: '#181c23',
            paper: '#23272f',
          },
          primary: { main: '#00bcd4' },
          secondary: { main: '#90caf9' },
          accent: { main: '#00bcd4' },
        }),
  },
  shape: {
    borderRadius: 14,
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeightBold: 700,
    h6: { fontWeight: 700 },
  },
});

type PatchType = {
  sid: string;
  sourceHome: string;
  targetHome: string;
  patchFolder: string;
  platform: string;
  patchOptions: PatchOptionsState;
  download: 'NO' | 'YES';
  restoration: boolean;
  tdeEnabled: boolean;
  useProxy: boolean;
  proxyHost: string;
  proxyPort: string;
  sourceTnsAdminEnabled?: boolean;
  sourceTnsAdminDir?: string;
  targetTnsAdminEnabled?: boolean;
  targetTnsAdminDir?: string;
  upgradeNodeEnabled?: boolean;
  upgradeNodeValue?: string;
  upgradeType: string;
  distributedUpgrade: boolean;
  targetCdbSid?: string;
  newPdbName?: string;
  fileNameConvertOption?: string;
  sourcePath?: string;
  targetPath?: string;
  sourcePdb?: string;
  targetPdbName?: string;
  dbLink?: string;
  refreshOption?: string;
  refreshInterval?: string;
  parallelPdbCreation?: string;
  dropDbLink?: boolean;
  startTimeType?: 'now' | 'delay' | 'datetime';
  startTimeValue?: string;
  winCredential?: string;
  deleteWinCredentialFile?: boolean;
  logDir?: string;
  targetVersionEnabled?: boolean;
  targetVersion?: string;
  targetPlatform?: string;
  sourceHomeTouched?: boolean;
  targetHomeTouched?: boolean;
  sourceHomeEnabled?: boolean;
  oracleBaseEnabled?: boolean;
  oracleBase?: string;
  editionEnabled?: boolean;
  edition?: string;
  dropGrpAfterUpgrade?: boolean;
  dropGrpAfterPatching?: boolean;
  timezoneUpgUpg?: boolean;
  runUtlrpUpg?: boolean;
  removeUnderscoreUpg?: boolean;
  raiseCompatibleUpg?: boolean;
  raiseCompatibleUpgValue?: string;
  delDuringUpgradePfile?: string;
  addAfterUpgradePfile?: string;
  delDuringUpgradePfileEnabled?: boolean;
  addAfterUpgradePfileEnabled?: boolean;
  proactiveFixups?: boolean;
  delAfterUpgradePfile?: string;
  delAfterUpgradePfileEnabled?: boolean;
  addDuringUpgradePfile?: string;
  addDuringUpgradePfileEnabled?: boolean;
  emTargetNameEnabled?: boolean;
  emTargetName?: string;
  emcliPathEnabled?: boolean;
  emcliPath?: string;
  emBlackoutSuffixEnabled?: boolean;
  emBlackoutSuffix?: string;
  oraInventoryEnabled?: boolean;
  oraInventory?: string;
  inventoryGroupEnabled?: boolean;
  inventoryGroup?: string;
  binaryOptionRac?: boolean;
  clusterNodesEnabled?: boolean;
  clusterNodes?: string;
  racRolling?: 'DISABLED' | 'REQUIRED' | 'FORCE' | 'AUTO';
  drainTimeout?: string;
  ignorePrereqFailure?: boolean;
  // Home Settings - new fields
  hsAccountType?: string;
  hsBinoptAsm?: boolean;
  hsBinoptDnfs?: boolean;
  hsBinoptDm?: boolean;
  hsBinoptJox?: boolean;
  hsBinoptOlap?: boolean;
  hsBinoptPart?: boolean;
  hsBinoptRat?: boolean;
  hsBinoptSdo?: boolean;
  hsBinoptUniaud?: boolean;
  hsHomeName?: string;
  hsIgnoreOpatchConflict?: 'error' | 'keep_first' | 'skip_all';
  hsOsdbaGroup?: string;
  hsOsoperGroup?: string;
  hsOsbackupdbaGroup?: string;
  hsOsdgdbaGroup?: string;
  hsOskmdbaGroup?: string;
  hsOsracdbaGroup?: string;
  hsReadOnly?: boolean;
  hsRuApply?: boolean;
  hsEnvTmp?: string;
  manageStandbysClause?: string;
  passthroughParams?: Record<string, string>;
  existingOracleHome?: boolean;
};

const defaultPatch = (idx = 1, isUpgrade = false): PatchType => ({
  sid: '',
  sourceHome: '',
  targetHome: '',
  patchFolder: '',
  platform: 'LINUX.X64',
  patchOptions: {
    recommended: false,
    recommendedVersion: '',
    useGoldImageFile: false,
    goldImageFile: '',
    createGoldImage: '',
    ru: true,
    ruVersion: '',
    ojvm: false,
    ojvmVersion: '',
    opatch: false,
    dpbp: false,
    au: false,
    ahf: false,
    cvu: false,
    sqlcl: false,
    tools: false,
    jdk: false,
    specificPatches: '',
    ocwVersion: '',
    goldImage: 'AUTO',
  },
  download: 'NO',
  restoration: true,
  tdeEnabled: false,
  useProxy: false,
  proxyHost: '',
  proxyPort: '',
  sourceTnsAdminEnabled: false,
  sourceTnsAdminDir: '',
  targetTnsAdminEnabled: false,
  targetTnsAdminDir: '',
  upgradeNodeEnabled: false,
  upgradeNodeValue: '',
  upgradeType: 'regular',
  distributedUpgrade: false,
  targetCdbSid: '',
  newPdbName: '',
  fileNameConvertOption: 'convert',
  sourcePath: '',
  targetPath: '',
  sourcePdb: '',
  targetPdbName: '',
  dbLink: '',
  refreshOption: 'clone_only',
  refreshInterval: '',
  parallelPdbCreation: '',
  dropDbLink: false,
  startTimeType: 'now',
  startTimeValue: '',
  winCredential: '',
  deleteWinCredentialFile: true,
  logDir: '',
  targetVersionEnabled: true,
  targetVersion: '19',
  targetPlatform: '',
  sourceHomeTouched: false,
  targetHomeTouched: false,
  sourceHomeEnabled: false,
  oracleBaseEnabled: false,
  oracleBase: '/u01/app/oracle',
  editionEnabled: false,
  edition: 'ee',
  dropGrpAfterUpgrade: false,
  dropGrpAfterPatching: false,
  timezoneUpgUpg: true,
  runUtlrpUpg: true,
  removeUnderscoreUpg: true,
  raiseCompatibleUpg: false,
  raiseCompatibleUpgValue: '',
  delDuringUpgradePfile: '',
  addAfterUpgradePfile: '',
  delDuringUpgradePfileEnabled: false,
  addAfterUpgradePfileEnabled: false,
  ...(isUpgrade ? { proactiveFixups: false } : {}),
  delAfterUpgradePfile: '',
  delAfterUpgradePfileEnabled: false,
  addDuringUpgradePfile: '',
  addDuringUpgradePfileEnabled: false,
  emTargetNameEnabled: false,
  emTargetName: '',
  emcliPathEnabled: false,
  emcliPath: '',
  emBlackoutSuffixEnabled: false,
  emBlackoutSuffix: '',
  oraInventoryEnabled: false,
  oraInventory: '',
  inventoryGroupEnabled: false,
  inventoryGroup: '',
  binaryOptionRac: undefined,
  clusterNodesEnabled: false,
  clusterNodes: '',
  racRolling: 'AUTO',
  drainTimeout: '',
  ignorePrereqFailure: false,
  hsAccountType: '',
  hsBinoptAsm: undefined,
  hsBinoptDnfs: undefined,
  hsBinoptDm: undefined,
  hsBinoptJox: undefined,
  hsBinoptOlap: undefined,
  hsBinoptPart: undefined,
  hsBinoptRat: undefined,
  hsBinoptSdo: undefined,
  hsBinoptUniaud: undefined,
  hsHomeName: '',
  hsIgnoreOpatchConflict: 'error' as const,
  hsOsdbaGroup: '',
  hsOsoperGroup: '',
  hsOsbackupdbaGroup: '',
  hsOsdgdbaGroup: '',
  hsOskmdbaGroup: '',
  hsOsracdbaGroup: '',
  hsReadOnly: false,
  hsRuApply: false,
  hsEnvTmp: '',
  manageStandbysClause: 'standbys=none',
  passthroughParams: {},
  existingOracleHome: false,
});

function App() {
  const [themeMode, setThemeMode] = React.useState<'light' | 'dark'>('dark');
  const [versionConfig, setVersionConfig] = React.useState<VersionConfig>(DEFAULT_VERSION_CONFIG);
  React.useEffect(() => {
    loadVersionConfig().then(setVersionConfig);
  }, []);

  const [operationType, setOperationType] = React.useState<'patch' | 'upgrade'>('patch');
  const [globalLogDir, setGlobalLogDir] = React.useState('');
  const [dbTabs, setDbTabs] = React.useState([{ key: 'patch1', label: 'patch1' }]);
  const [activeDbTab, setActiveDbTab] = React.useState('patch1');
  const [activeSubTabs, setActiveSubTabs] = React.useState<{ [tabKey: string]: number }>({ patch1: 0 });
  const [patches, setPatches] = React.useState([defaultPatch(1)]);
  const [keystorePath, setKeystorePath] = React.useState('');
  const BASIC_PLACEHOLDERS = React.useMemo(() => ({
    sid: 'DB1',
    source_home: '/u01/app/oracle/product/19.15/dbhome_1',
    target_home: `/u01/app/oracle/product/${getTargetVersion(versionConfig, '19')}/dbhome_1`,
    patch_folder: '/home/oracle/autoupgrade/patches',
    win_log_dir: 'C:\\Oracle\\AutoUpgrade\\Log',
    win_upgrade_log_dir: 'C:\\Oracle\\AutoUpgrade\\Upgrade\\Log',
    win_patch_folder: 'C:\\Oracle\\AutoUpgrade\\Patches',
    win_keystore: 'C:\\Oracle\\AutoUpgrade\\Keystore'
  }), [versionConfig]);

  const theme = React.useMemo(() => createTheme(getDesignTokens(themeMode)), [themeMode]);

  const toggleTheme = () => {
    setThemeMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const accentColor = theme.palette.mode === 'light' ? '#003366' : '#00bcd4';

  const cardSx = {
    p: 4,
    borderRadius: 4,
    boxShadow: '0 2px 16px 0 rgba(0,0,0,0.08)',
    mb: 6,
    width: '100%',
    maxWidth: 1200,
    // ml: 0, // Remove or ensure this is set to 0
  };

  const sectionHeaderSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    mb: 3,
    color: accentColor,
    fontWeight: 600,
    fontSize: '1rem',
  };

  // Define a common border style for all main sections
  const sectionBorderSx = {
    border: `2.5px solid ${accentColor}`,
    boxShadow: '0 8px 32px 0 rgba(0,188,212,0.08)',
  };

  // Store last loaded config to apply after operationType changes
  const lastLoadedConfig = useRef<any>(null);

  // Tab logic
  const handleAddDbTab = () => {
    const nextNum = dbTabs.length + 1;
    const prefix = operationType === 'upgrade' ? 'upg' : 'patch';
    const newKey = `${prefix}${nextNum}`;
    setDbTabs([...dbTabs, { key: newKey, label: newKey }]);
    setPatches([...patches, defaultPatch(nextNum)]);
    setActiveSubTabs(prev => ({ ...prev, [newKey]: 0 }));
    setActiveDbTab(newKey);
  };

  // When switching tabs, default to subtab 0 if not set
  const currentSubTab = activeSubTabs[activeDbTab] ?? 0;
  const handleRemoveDbTab = (key: string) => {
    const idx = dbTabs.findIndex(tab => tab.key === key);
    const newTabs = dbTabs.filter(tab => tab.key !== key);
    const newPatches = patches.filter((_, i) => i !== idx);
    setDbTabs(newTabs);
    setPatches(newPatches);
    if (activeDbTab === key && newTabs.length > 0) {
      setActiveDbTab(newTabs[Math.max(0, idx - 1)].key);
    }
  };
  const handleChangeDbTab = (_: any, newValue: string) => {
    setActiveDbTab(newValue);
  };
  // Find current patch index
  const currentPatchIdx = dbTabs.findIndex(tab => tab.key === activeDbTab);
  const currentPatch = patches[currentPatchIdx];

      // Helper to update a field in the current patch
    const updateCurrentPatchField = (field: string, value: any) => {
      setPatches(prevPatches => {
        const newPatches = [...prevPatches]; // Create a new array reference
        newPatches[currentPatchIdx] = { ...newPatches[currentPatchIdx], [field]: value };
        return newPatches;
      });
  };

  // Helper to update patchOptions in the current patch
  const updateCurrentPatchOptions = (opts: any) => {
    setPatches(prevPatches => {
      const newPatches = [...prevPatches];
      newPatches[currentPatchIdx] = { 
        ...newPatches[currentPatchIdx], 
        patchOptions: { ...newPatches[currentPatchIdx].patchOptions, ...opts } 
      };
      return newPatches;
    });
  };

  const [globalOptionsOpen, setGlobalOptionsOpen] = React.useState(true);
  const [dbConfigsOpen, setDbConfigsOpen] = React.useState(true);
  const [executionMode, setExecutionMode] = React.useState<'download' | 'create_home' | 'upgrade' | 'analyze' | 'fixups' | 'deploy'>('deploy');

  // Memoize the renderValidationMessages function to prevent infinite re-renders
  const renderValidationMessages = useCallback((msgs: any[]) => {
    if (
      operationType === 'patch' &&
      (executionMode === 'create_home' || executionMode === 'download')
    ) {
      setValidationMessages(msgs.filter(m => m.type === 'success' || m.type === 'warning'));
    } else {
      setValidationMessages([]);
    }
  }, [operationType, executionMode]);

  // Extract all relevant fields for validation dependencies
  const sourceHome = currentPatch?.sourceHome;
  const sourceHomeEnabled = currentPatch?.sourceHomeEnabled;
  const oracleBase = currentPatch?.oracleBase;
  const oracleBaseEnabled = currentPatch?.oracleBaseEnabled;
  const edition = currentPatch?.edition;
  const editionEnabled = currentPatch?.editionEnabled;
  const targetVersion = currentPatch?.targetVersion;
  const targetVersionEnabled = currentPatch?.targetVersionEnabled;
  const patchOptions = React.useMemo(
    () => currentPatch?.patchOptions || {},
    [currentPatch?.patchOptions]
  );
  const ru = patchOptions.ru;
  const ruVersion = patchOptions.ruVersion;
  const ojvm = patchOptions.ojvm;
  const ojvmVersion = patchOptions.ojvmVersion;
  const mrp = patchOptions.mrp;
  const specificPatches = patchOptions.specificPatches;

  const [validationMessages, setValidationMessages] = React.useState<{ type: 'success' | 'warning'; content: React.ReactNode }[]>([]);

  // Validate that global_log_dir and keystore are not the same
  const keystoreValidationWarning = React.useMemo(() => {
    // Get the effective log dir (use placeholder if globalLogDir is empty)
    const isAnyWindows = patches.some(p => p.platform === 'WINDOWS.X64');
    const winLogDir = operationType === 'upgrade' ? 'C:\\Oracle\\AutoUpgrade\\Upgrade\\Log' : 'C:\\Oracle\\AutoUpgrade\\Log';
    const logDir = operationType === 'upgrade' 
      ? (isAnyWindows ? winLogDir : '/home/oracle/autoupgrade/log') 
      : (isAnyWindows ? winLogDir : '/home/oracle/autoupgrade/log');
    const effectiveGlobalLogDir = globalLogDir && globalLogDir.trim() ? globalLogDir.trim() : logDir;
    const effectiveKeystorePath = keystorePath && keystorePath.trim() ? keystorePath.trim() : '';
    
    const shouldShowWarning =
      operationType === 'patch' &&
      (executionMode === 'download' || executionMode === 'analyze' || executionMode === 'deploy' || executionMode === 'fixups') &&
      effectiveKeystorePath &&
      effectiveGlobalLogDir === effectiveKeystorePath;

    if (shouldShowWarning) {
      return {
        type: 'warning' as const,
        content: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: 22, verticalAlign: 'middle' }}>⚠️</span>
            <Typography sx={{ color: '#ff5252', fontSize: 15, fontWeight: 500 }}>
              Global Log Directory (<b>global_log_dir</b>) and Keystore Path (<b>keystore</b>) can't be the same.
            </Typography>
          </Box>
        )
      };
    }
    return null;
  }, [operationType, executionMode, globalLogDir, keystorePath, patches]);

  const isInvalidConfig =
    operationType === 'patch' &&
    (executionMode === 'create_home' || executionMode === 'download' || executionMode === 'analyze' || executionMode === 'deploy' || executionMode === 'fixups') &&
    validationMessages.some(m => m.type === 'warning');

  React.useEffect(() => {
    if (!currentPatch) return;
    const baseMessages = getPatchValidationMessages({
      downloadMode: executionMode === 'download' || executionMode === 'create_home',
      executionMode,
      patchKey: activeDbTab,
      editionEnabled: editionEnabled ?? false,
      oracleBaseEnabled: oracleBaseEnabled ?? false,
      patchOptions: patchOptions,
      targetVersionEnabled,
      sourceHomeEnabled,
      sourceHome,
    });
    
    // Add keystore warning if it exists
    const allMessages = keystoreValidationWarning 
      ? [...baseMessages, keystoreValidationWarning]
      : baseMessages;
    
    setValidationMessages(allMessages);
  }, [
    executionMode,
          activeDbTab,
      currentPatchIdx,
      sourceHome,
    sourceHomeEnabled,
    oracleBase,
    oracleBaseEnabled,
    edition,
    editionEnabled,
    targetVersion,
    targetVersionEnabled,
    ru,
    ruVersion,
    ojvm,
    ojvmVersion,
    mrp,
    specificPatches,
    currentPatch,
    patchOptions,
    keystoreValidationWarning
  ]);

  // Config generation
  const [configText, setConfigText] = React.useState('');

  const executionModes = [
    {
      value: 'analyze',
      label: 'Analyze Only',
      tooltip: 'Performs prechecks only',
    },
    {
      value: 'fixups',
      label: 'Fixups',
      tooltip: 'Executes fixup scripts',
    },
    {
      value: 'download',
      label: 'Download',
      tooltip: 'Downloads patches only',
    },
    {
      value: 'create_home',
      label: 'Create Home',
      tooltip: 'Creates target home only',
    },
    {
      value: 'deploy',
      label: 'Deploy',
      tooltip: 'Full patching operation',
    },
  ];

  // Filter execution modes based on operationType
  const filteredExecutionModes = operationType === 'upgrade'
    ? executionModes.filter(m => ['analyze', 'fixups', 'deploy'].includes(m.value)).map(m =>
        m.value === 'deploy'
          ? { ...m, tooltip: 'Full upgrade operation' }
          : m
      )
    : executionModes;

  // Determine which setup box to show
  const anyDownload = patches.some(p => p.download === 'YES');
  const anyTDE = patches.some(p => p.tdeEnabled);
  const showBoth = anyDownload && anyTDE && executionMode !== 'create_home';
  const showDownload = anyDownload && !anyTDE && executionMode !== 'create_home';
  const showTDE = anyTDE && !anyDownload;

  React.useEffect(() => {
    // When executionMode changes, set download to 'YES' for 'download' and 'create_home' only.
    if (executionMode === 'download' || executionMode === 'create_home') {
      setPatches(patches => patches.map((p, i) => i === currentPatchIdx ? { ...p, download: 'YES' } : p));
    }
  }, [executionMode, currentPatchIdx]);

  // Auto-update target home based on target version for create_home mode
  React.useEffect(() => {
    if (executionMode === 'create_home' && currentPatch && currentPatch.targetVersion) {
      const isWindows = currentPatch.platform === 'WINDOWS.X64';
      let newTargetHome = '';
      
      switch (currentPatch.targetVersion) {
        case '19':
          newTargetHome = isWindows ? `C:\\Oracle\\Product\\${getTargetVersion(versionConfig, '19')}\\dbhome_1` : `/u01/app/oracle/product/${getTargetVersion(versionConfig, '19')}/dbhome_1`;
          break;
        case '21':
          newTargetHome = isWindows ? `C:\\Oracle\\Product\\${getTargetVersion(versionConfig, '21')}\\dbhome_1` : `/u01/app/oracle/product/${getTargetVersion(versionConfig, '21')}/dbhome_1`;
          break;
        case '23':
        case '26':
          newTargetHome = isWindows ? `C:\\Oracle\\Product\\${getTargetVersion(versionConfig, '23')}\\dbhome_1` : `/u01/app/oracle/product/${getTargetVersion(versionConfig, '23')}/dbhome_1`;
          break;
        default:
          newTargetHome = isWindows ? `C:\\Oracle\\Product\\${getTargetVersion(versionConfig, '19')}\\dbhome_1` : `/u01/app/oracle/product/${getTargetVersion(versionConfig, '19')}/dbhome_1`;
      }
      
      // Only update if target home hasn't been manually touched by the user
      if (!currentPatch.targetHomeTouched) {
        setPatches(prevPatches => {
          const newPatches = [...prevPatches];
          newPatches[currentPatchIdx] = { ...newPatches[currentPatchIdx], targetHome: newTargetHome };
          return newPatches;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionMode, currentPatch?.targetVersion, currentPatch?.platform, currentPatchIdx, currentPatch?.targetHomeTouched]);

  // Auto-update target home based on RU version for create_home mode
  React.useEffect(() => {
    if ((executionMode === 'create_home' || executionMode === 'download') && currentPatch && currentPatch.patchOptions.ru && currentPatch.patchOptions.ruVersion) {
      const isWindows = currentPatch.platform === 'WINDOWS.X64';
      const ruVersion = currentPatch.patchOptions.ruVersion;
      let newTargetHome = '';
      
      // Extract major version from RU version (e.g., "21.15" -> "21")
      if (/^21(\.|$)/.test(ruVersion)) {
        newTargetHome = isWindows ? `C:\\Oracle\\Product\\${getTargetVersion(versionConfig, '21')}\\dbhome_1` : `/u01/app/oracle/product/${getTargetVersion(versionConfig, '21')}/dbhome_1`;
        // Also update target version to 21
        if (executionMode === 'create_home' && currentPatch.targetVersion !== '21') {
          setPatches(prevPatches => {
            const newPatches = [...prevPatches];
            newPatches[currentPatchIdx] = { ...newPatches[currentPatchIdx], targetVersion: '21', targetHome: newTargetHome };
            return newPatches;
          });
          return;
        }
      } else if (/^23(\.|$)/.test(ruVersion)) {
        // For version 23, use the specific RU version in the path (e.g., 23.9, 23.15)
        newTargetHome = isWindows ? `C:\\Oracle\\Product\\${ruVersion}\\dbhome_1` : `/u01/app/oracle/product/${ruVersion}/dbhome_1`;
        // Also update target version to 23
        if (executionMode === 'create_home' && currentPatch.targetVersion !== '23') {
          setPatches(prevPatches => {
            const newPatches = [...prevPatches];
            newPatches[currentPatchIdx] = { ...newPatches[currentPatchIdx], targetVersion: '23', targetHome: newTargetHome };
            return newPatches;
          });
          return;
        }
      } else if (/^26(\.|$)/.test(ruVersion)) {
        // For version 26, use the specific RU version in the path (same behavior as 23)
        newTargetHome = isWindows ? `C:\\Oracle\\Product\\${ruVersion}\\dbhome_1` : `/u01/app/oracle/product/${ruVersion}/dbhome_1`;
        // Also update target version to 26
        if (executionMode === 'create_home' && currentPatch.targetVersion !== '26') {
          setPatches(prevPatches => {
            const newPatches = [...prevPatches];
            newPatches[currentPatchIdx] = { ...newPatches[currentPatchIdx], targetVersion: '26', targetHome: newTargetHome };
            return newPatches;
          });
          return;
        }
      } else if (/^19(\.|$)/.test(ruVersion)) {
        newTargetHome = isWindows ? `C:\\Oracle\\Product\\${getTargetVersion(versionConfig, '19')}\\dbhome_1` : `/u01/app/oracle/product/${getTargetVersion(versionConfig, '19')}/dbhome_1`;
        // Also update target version to 19
        if (executionMode === 'create_home' && currentPatch.targetVersion !== '19') {
          setPatches(prevPatches => {
            const newPatches = [...prevPatches];
            newPatches[currentPatchIdx] = { ...newPatches[currentPatchIdx], targetVersion: '19', targetHome: newTargetHome };
            return newPatches;
          });
          return;
        }
      }
      
      // Only update target home if it hasn't been manually touched by the user
      if (!currentPatch.targetHomeTouched && newTargetHome) {
        setPatches(prevPatches => {
          const newPatches = [...prevPatches];
          newPatches[currentPatchIdx] = { ...newPatches[currentPatchIdx], targetHome: newTargetHome };
          return newPatches;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionMode, currentPatch?.patchOptions.ru, currentPatch?.patchOptions.ruVersion, currentPatch?.platform, currentPatchIdx, currentPatch?.targetHomeTouched]);

  React.useEffect(() => {
    let text = `# Created by AutoUpgrade Composer\n`;
    if (operationType === 'patch') {
      let execLabel = '';
      switch (executionMode) {
        case 'analyze': execLabel = 'Analyze'; break;
        case 'fixups': execLabel = 'Fixups'; break;
        case 'download': execLabel = 'Download'; break;
        case 'create_home': execLabel = 'CreateHome'; break;
        case 'deploy': execLabel = 'Deploy'; break;
        default: execLabel = executionMode ? executionMode.charAt(0).toUpperCase() + executionMode.slice(1) : '';
      }
      text += `# Patch, ExecMode: ${execLabel}\n`;
    } else if (operationType === 'upgrade') {
      let upgTypeLabel = patches[0]?.upgradeType || 'regular';
      text += `# Upgrade, UpgType: ${upgTypeLabel}`;
      
      // Add FileNameConvert info for noncdb2cdb and pdbrelocation
      if (patches[0]?.upgradeType === 'noncdb2cdb' || patches[0]?.upgradeType === 'pdbrelocation') {
        const fileNameConvert = patches[0]?.fileNameConvertOption || 'convert';
        if (fileNameConvert === 'convert') {
          text += `, FileNameConvert: paths`;
        } else if (fileNameConvert === 'none') {
          text += `, FileNameConvert: none`;
        } else if (fileNameConvert === 'nocopy') {
          text += `, FileNameConvert: nocopy`;
        }
      }
      // Add Refresh info for pdbrelocation
      if (patches[0]?.upgradeType === 'pdbrelocation') {
        const refreshOption = patches[0]?.refreshOption;
        if (refreshOption === 'interval') {
          text += `, Refresh: interval`;
        } else {
          text += `, Refresh: none`;
        }
      }
      text += '\n';
    }
    text += '\n';
    const isAnyWindows = patches.some(p => p.platform === 'WINDOWS.X64');
    const winLogDir = operationType === 'upgrade' ? 'C:\\Oracle\\AutoUpgrade\\Upgrade\\Log' : 'C:\\Oracle\\AutoUpgrade\\Log';
    const logDir = operationType === 'upgrade' 
      ? (isAnyWindows ? winLogDir : '/home/oracle/autoupgrade/log') 
      : (isAnyWindows ? winLogDir : '/home/oracle/autoupgrade/log');

    // Download mode: only output the allowed parameters
    if (executionMode === 'download' || executionMode === 'create_home') {
      text += `global.global_log_dir=${globalLogDir || logDir}\n`;
      const defaultKeystore = isAnyWindows ? 'C:\\Oracle\\AutoUpgrade\\Keystore' : '/home/oracle/autoupgrade/keystore';
      // Only add keystore if NOT PatchSection create_home mode
      if (!(operationType === 'patch' && executionMode === 'create_home')) {
        text += `global.keystore=${keystorePath || defaultKeystore}\n`;
      }
      text += `\n`;
      patches.forEach((patch, idx) => {
        const patchNum = idx + 1;
        const tabLabel = dbTabs[idx]?.label || (operationType === 'upgrade' ? `upg${patchNum}` : `patch${patchNum}`);
        const prefix = tabLabel;
        // Debug: log mapping for each patch
        // SID (only output if present in the config)
        if (patch.sid) {
          text += `${prefix}.sid=${patch.sid}\n`;
        }
        // Log Directory (path: .../autoupgrade/log/<...>)
        const sidPart = operationType === 'upgrade' ? `${patch.sid || `DB${idx + 1}`}_Upgrade` : (patch.sid || `DB${idx + 1}`);
        const logDirPlaceholderSid = patch.platform === 'WINDOWS.X64'
          ? `C:\\Oracle\\AutoUpgrade\\Log\\${sidPart}`
          : `/home/oracle/autoupgrade/log/${sidPart}`;
        const logDirValue = !patch.logDir || patch.logDir === 'undefined' ? logDirPlaceholderSid : patch.logDir;
        text += `${prefix}.log_dir=${logDirValue}\n`;
        // Folder
        text += `${prefix}.folder=${patch.patchFolder || (isAnyWindows ? 'C:\\Oracle\\AutoUpgrade\\Patches' : '/home/oracle/autoupgrade/patches')}\n`;
        // Source Home (if enabled) - only for non-create_home mode
        if (patch.sourceHomeEnabled && executionMode !== 'create_home') {
          const dlTv = patch.targetVersion;
          const dlIsV23or26 = dlTv === '23' || dlTv === '26';
          const dlSrcFallback = dlIsV23or26 ? (isAnyWindows ? `C:\\Oracle\\Product\\${getSourceVersion(versionConfig, '23')}\\dbhome_1` : `/u01/app/oracle/product/${getSourceVersion(versionConfig, '23')}/dbhome_1`)
            : dlTv === '21' ? (isAnyWindows ? 'C:\\Oracle\\Product\\21.3\\dbhome_1' : '/u01/app/oracle/product/21.3/dbhome_1')
            : (isAnyWindows ? 'C:\\Oracle\\Product\\19.15\\dbhome_1' : '/u01/app/oracle/product/19.15/dbhome_1');
          text += `${prefix}.source_home=${patch.sourceHome || dlSrcFallback}\n`;
        }
        // Patch
        const getPatchConfig = (opts: any) => {
          if (opts.useGoldImageFile) {
            const tv = patch.targetVersionEnabled ? (patch.targetVersion || '19') : '19';
            const ruVer = (versionConfig?.targetVersions as any)?.[tv] || '19.31';
            const folder = isAnyWindows ? (patch.patchFolder || 'C:\\Oracle\\AutoUpgrade\\Patches') : (patch.patchFolder || '/home/oracle/autoupgrade/patches');
            return `GOLDIMAGE:${opts.goldImageFile || `${folder}/my_image_${ruVer}.zip`}`;
          }
          if (opts.recommended) {
            const parts = [opts.recommendedVersion ? `RECOMMENDED:${opts.recommendedVersion}` : 'RECOMMENDED'];
            if (opts.tools) {
              parts.push('TOOLS');
            } else {
              if (opts.au) parts.push('AU');
              if (opts.ahf) parts.push('AHF');
              if (opts.cvu) parts.push('CVU');
              if (opts.sqlcl) parts.push('SQLCL');
            }
            if (opts.mrp) parts.push('MRP');
            if (opts.ocw) parts.push('OCW');
            if (opts.jdk) parts.push('JDK');
            if (opts.sdo) parts.push('SDOBP');
            if (opts.text) parts.push('TEXT');
            if (opts.specificPatches && opts.specificPatches.trim()) parts.push(opts.specificPatches.trim());
            return parts.join(',');
          }
          const parts = [];
          if (opts.ru) parts.push(opts.ruVersion ? `RU:${opts.ruVersion}` : 'RU');
          if (opts.ojvm) parts.push(opts.ojvmVersion ? `OJVM:${opts.ojvmVersion}` : 'OJVM');
          if (opts.dpbp) parts.push('DPBP');
          if (opts.jdk) parts.push('JDK');
          if (opts.ocw) parts.push(opts.ocwVersion ? `OCW:${opts.ocwVersion}` : 'OCW');
          if (opts.sdo) parts.push('SDOBP');
          if (opts.text) parts.push('TEXT');
          if (opts.mrp) parts.push('MRP');
          if (opts.tools) {
            parts.push('TOOLS');
          } else {
            if (opts.opatch) parts.push('OPATCH');
            if (opts.au) parts.push('AU');
            if (opts.ahf) parts.push('AHF');
            if (opts.cvu) parts.push('CVU');
            if (opts.sqlcl) parts.push('SQLCL');
          }
          if (opts.specificPatches && opts.specificPatches.trim()) parts.push(opts.specificPatches.trim());
          return parts.join(',');
        };
        text += `${prefix}.patch=${getPatchConfig(patch.patchOptions)}\n`;
        // Gold Image (only for download mode, not AUTO, not ALL — ALL is the default)
        if (executionMode === 'download' && patch.patchOptions.goldImage && patch.patchOptions.goldImage !== 'AUTO' && patch.patchOptions.goldImage !== 'ALL') {
          text += `${prefix}.gold_image=${patch.patchOptions.goldImage}\n`;
        }
        if (executionMode === 'download' && (patch.patchOptions.goldImage === 'YES' || patch.patchOptions.goldImage === 'ALL') && patch.patchOptions.goldImageSecurityLevel && patch.patchOptions.goldImageSecurityLevel !== 'HIGH') {
          text += `${prefix}.gold_image.security_patch_level=${patch.patchOptions.goldImageSecurityLevel}\n`;
        }
        if (executionMode === 'create_home' && !patch.patchOptions.useGoldImageFile && patch.patchOptions.createGoldImage) {
          text += `${prefix}.create_gold_image=${patch.patchOptions.createGoldImage}\n`;
        }
        // Target Version (if enabled)
        if (patch.targetVersionEnabled) {
          text += `${prefix}.target_version=${patch.targetVersion || '19'}\n`;
        }
        // Platform (not for create_home)
        if (executionMode !== 'create_home') {
          text += `${prefix}.platform=${patch.platform || ''}\n`;
        }
        // Always add Target Home for create_home
        if (executionMode === 'create_home') {
          // Add source_home if enabled
          if (patch.sourceHomeEnabled) {
            const chTv = patch.targetVersion;
          const chIsV23or26 = chTv === '23' || chTv === '26';
          const chWin = patch.platform === 'WINDOWS.X64';
          const sourceHomeValue = patch.sourceHome || (chIsV23or26 ? (chWin ? `C:\\Oracle\\Product\\${getSourceVersion(versionConfig, '23')}\\dbhome_1` : `/u01/app/oracle/product/${getSourceVersion(versionConfig, '23')}/dbhome_1`)
            : chTv === '21' ? (chWin ? 'C:\\Oracle\\Product\\21.3\\dbhome_1' : '/u01/app/oracle/product/21.3/dbhome_1')
            : (chWin ? 'C:\\Oracle\\Product\\19.15\\dbhome_1' : '/u01/app/oracle/product/19.15/dbhome_1'));
            text += `${prefix}.source_home=${sourceHomeValue}\n`;
          }
          const chTv2 = patch.targetVersion;
          const chIsV23or262 = chTv2 === '23' || chTv2 === '26';
          const chWin2 = patch.platform === 'WINDOWS.X64';
          const targetHomeValue = patch.targetHome || (chIsV23or262 ? (chWin2 ? `C:\\Oracle\\Product\\${getTargetVersion(versionConfig, '23')}\\dbhome_1` : `/u01/app/oracle/product/${getTargetVersion(versionConfig, '23')}/dbhome_1`)
            : chTv2 === '21' ? (chWin2 ? `C:\\Oracle\\Product\\${getTargetVersion(versionConfig, '21')}\\dbhome_1` : `/u01/app/oracle/product/${getTargetVersion(versionConfig, '21')}/dbhome_1`)
            : (chWin2 ? `C:\\Oracle\\Product\\${getTargetVersion(versionConfig, '19')}\\dbhome_1` : `/u01/app/oracle/product/${getTargetVersion(versionConfig, '19')}/dbhome_1`));
          text += `${prefix}.target_home=${targetHomeValue}\n`;
          // Collect home_settings params and output them together
          const homeSettings = [];
          if (patch.editionEnabled && patch.edition) {
            homeSettings.push(`${prefix}.home_settings.edition=${patch.edition}`);
          }
          if (patch.oracleBaseEnabled && patch.oracleBase) {
            homeSettings.push(`${prefix}.home_settings.oracle_base=${patch.oracleBase}`);
          }
          // Add oraInventory and Inventory Group if enabled
          if (patch.oraInventoryEnabled && patch.platform !== 'WINDOWS.X64') {
            const oraInventoryValue = patch.oraInventory && patch.oraInventory.trim() !== ''
              ? patch.oraInventory
              : (patch.platform === 'WINDOWS.X64' ? 'C:\\Oracle\\OraInventory' : '/u01/app/oraInventory');
            homeSettings.push(`${prefix}.home_settings.inventory_location=${oraInventoryValue}`);
          }
          if (patch.inventoryGroupEnabled && patch.platform !== 'WINDOWS.X64') {
            const inventoryGroupValue = patch.inventoryGroup && patch.inventoryGroup.trim() !== ''
              ? patch.inventoryGroup
              : 'oinstall';
            homeSettings.push(`${prefix}.home_settings.inventory_group=${inventoryGroupValue}`);
          }
          // Add Ignore PreReq Failure if enabled
          if (patch.ignorePrereqFailure) {
            homeSettings.push(`${prefix}.home_settings.ignore_prereq_failure=YES`);
          }
          // Binary Options: write =yes when explicitly ON, =no when explicitly OFF
          if (patch.binaryOptionRac === true) homeSettings.push(`${prefix}.home_settings.binopt.rac=yes`);
          else if (patch.binaryOptionRac === false) homeSettings.push(`${prefix}.home_settings.binopt.rac=no`);
          const writeBinopt = (val: boolean | undefined, key: string, winExclude?: boolean) => {
            if (winExclude && patch.platform === 'WINDOWS.X64') return;
            if (val === true) homeSettings.push(`${prefix}.home_settings.binopt.${key}=yes`);
            else if (val === false) homeSettings.push(`${prefix}.home_settings.binopt.${key}=no`);
          };
          writeBinopt(patch.hsBinoptAsm, 'asm', true);
          writeBinopt(patch.hsBinoptDnfs, 'dnfs');
          writeBinopt(patch.hsBinoptDm, 'dm', true);
          writeBinopt(patch.hsBinoptJox, 'jox', true);
          writeBinopt(patch.hsBinoptOlap, 'olap');
          writeBinopt(patch.hsBinoptPart, 'part');
          writeBinopt(patch.hsBinoptRat, 'rat');
          writeBinopt(patch.hsBinoptSdo, 'sdo', true);
          writeBinopt(patch.hsBinoptUniaud, 'uniaud');
          // Cluster nodes
          if (patch.clusterNodesEnabled) {
            const clusterNodesValue = patch.clusterNodes && patch.clusterNodes.trim() !== ''
              ? patch.clusterNodes
              : 'node1,node2,node3';
            homeSettings.push(`${prefix}.home_settings.cluster_nodes=${clusterNodesValue}`);
          }
          // Home name (Linux only)
          if (patch.hsHomeName && patch.hsHomeName.trim() && patch.platform !== 'WINDOWS.X64') {
            homeSettings.push(`${prefix}.home_settings.home_name=${patch.hsHomeName.trim()}`);
          }
          // ignore_opatch_conflict (only if not default 'error')
          if (patch.hsIgnoreOpatchConflict && patch.hsIgnoreOpatchConflict !== 'error') {
            homeSettings.push(`${prefix}.home_settings.ignore_opatch_conflict=${patch.hsIgnoreOpatchConflict}`);
          }
          // OS groups (Linux only, only if user provided value)
          if (patch.hsOsdbaGroup && patch.hsOsdbaGroup.trim() && patch.platform !== 'WINDOWS.X64') homeSettings.push(`${prefix}.home_settings.osdba_group=${patch.hsOsdbaGroup.trim()}`);
          if (patch.hsOsoperGroup && patch.hsOsoperGroup.trim() && patch.platform !== 'WINDOWS.X64') homeSettings.push(`${prefix}.home_settings.osoper_group=${patch.hsOsoperGroup.trim()}`);
          if (patch.hsOsbackupdbaGroup && patch.hsOsbackupdbaGroup.trim() && patch.platform !== 'WINDOWS.X64') homeSettings.push(`${prefix}.home_settings.osbackupdba_group=${patch.hsOsbackupdbaGroup.trim()}`);
          if (patch.hsOsdgdbaGroup && patch.hsOsdgdbaGroup.trim() && patch.platform !== 'WINDOWS.X64') homeSettings.push(`${prefix}.home_settings.osdgdba_group=${patch.hsOsdgdbaGroup.trim()}`);
          if (patch.hsOskmdbaGroup && patch.hsOskmdbaGroup.trim() && patch.platform !== 'WINDOWS.X64') homeSettings.push(`${prefix}.home_settings.oskmdba_group=${patch.hsOskmdbaGroup.trim()}`);
          if (patch.hsOsracdbaGroup && patch.hsOsracdbaGroup.trim() && patch.platform !== 'WINDOWS.X64') homeSettings.push(`${prefix}.home_settings.osracdba_group=${patch.hsOsracdbaGroup.trim()}`);
          // read_only (only if yes)
          if (patch.hsReadOnly) homeSettings.push(`${prefix}.home_settings.read_only=yes`);
          // ru_apply: only write if explicitly YES and RAC is not ON (RAC=ON makes ru_apply=yes implicit)
          if (patch.hsRuApply && patch.binaryOptionRac !== true) homeSettings.push(`${prefix}.home_settings.ru_apply=yes`);
          // account_type (Windows only)
          if (patch.hsAccountType && patch.hsAccountType.trim() && patch.platform === 'WINDOWS.X64') {
            homeSettings.push(`${prefix}.home_settings.account_type=${patch.hsAccountType.trim()}`);
          }
          if (homeSettings.length > 0) {
            text += homeSettings.join('\n') + '\n';
          }
          // env=TMP (separate format, outside homeSettings array)
          if (patch.hsEnvTmp && patch.hsEnvTmp.trim()) {
            text += `${prefix}.env=TMP=${patch.hsEnvTmp.trim()}\n`;
          }
        }
      });
      setConfigText(text);
      return;
    }
    // ... existing code for other modes ...

    text += `global.global_log_dir=${globalLogDir || logDir}\n`;
    // Only add global.keystore once if any patch needs it
    let keystoreNeeded = patches.some(p => (p.download === 'YES' && !p.patchOptions.useGoldImageFile) || p.tdeEnabled);
    if (keystoreNeeded) {
      const defaultKeystore = isAnyWindows ? 'C:\\Oracle\\AutoUpgrade\\Keystore' : '/home/oracle/autoupgrade/keystore';
      text += `global.keystore=${keystorePath || defaultKeystore}\n`;
    }
    text += `\n`;

    function formatDateTimeForConfig(raw: string) {
      // Expects 14 digits: ddmmyyyyhhmmss
      const v = raw.replace(/\D/g, '').slice(0, 14);
      if (v.length !== 14) return raw;
      return `${v.slice(0,2)}/${v.slice(2,4)}/${v.slice(4,8)} ${v.slice(8,10)}:${v.slice(10,12)}:${v.slice(12,14)}`;
    }

    function isDateInPast(raw: string) {
      if (!raw || raw.length !== 14) return false;
      const d = raw;
      const inputDate = new Date(
        Number(d.slice(4, 8)), // year
        Number(d.slice(2, 4)) - 1, // month (0-based)
        Number(d.slice(0, 2)), // day
        Number(d.slice(8, 10)), // hour
        Number(d.slice(10, 12)), // min
        Number(d.slice(12, 14)) // sec
      );
      return inputDate.getTime() < Date.now();
    }

    patches.forEach((patch, idx) => {
      const patchNum = idx + 1;
      const tabLabel = dbTabs[idx]?.label || (operationType === 'upgrade' ? `upg${patchNum}` : `patch${patchNum}`);
      const prefix = tabLabel;
      const getPatchConfig = (opts: any) => {
        if (opts.useGoldImageFile) {
          const tv = patch.targetVersionEnabled ? (patch.targetVersion || '19') : '19';
          const ruVer = (versionConfig?.targetVersions as any)?.[tv] || '19.31';
          const isWin = patch.platform === 'WINDOWS.X64';
          const folder = isWin ? (patch.patchFolder || 'C:\\Oracle\\AutoUpgrade\\Patches') : (patch.patchFolder || '/home/oracle/autoupgrade/patches');
          return `GOLDIMAGE:${opts.goldImageFile || `${folder}/my_image_${ruVer}.zip`}`;
        }
        if (opts.recommended) {
          const parts = [opts.recommendedVersion ? `RECOMMENDED:${opts.recommendedVersion}` : 'RECOMMENDED'];
          if (opts.tools) {
            parts.push('TOOLS');
          } else {
            if (opts.au) parts.push('AU');
            if (opts.ahf) parts.push('AHF');
            if (opts.cvu) parts.push('CVU');
            if (opts.sqlcl) parts.push('SQLCL');
          }
          if (opts.mrp) parts.push('MRP');
          if (opts.ocw) parts.push('OCW');
          if (opts.jdk) parts.push('JDK');
          if (opts.sdo) parts.push('SDOBP');
          if (opts.text) parts.push('TEXT');
          if (opts.specificPatches && opts.specificPatches.trim()) parts.push(opts.specificPatches.trim());
          return parts.join(',');
        }
        const parts = [];
        if (opts.ru) parts.push(opts.ruVersion ? `RU:${opts.ruVersion}` : 'RU');
        if (opts.ojvm) parts.push(opts.ojvmVersion ? `OJVM:${opts.ojvmVersion}` : 'OJVM');
        if (opts.dpbp) parts.push('DPBP');
        if (opts.jdk) parts.push('JDK');
        if (opts.ocw) parts.push(opts.ocwVersion ? `OCW:${opts.ocwVersion}` : 'OCW');
        if (opts.sdo) parts.push('SDOBP');
        if (opts.text) parts.push('TEXT');
        if (opts.mrp) parts.push('MRP');
        if (opts.tools) {
          parts.push('TOOLS');
        } else {
          if (opts.opatch) parts.push('OPATCH');
          if (opts.au) parts.push('AU');
          if (opts.ahf) parts.push('AHF');
          if (opts.cvu) parts.push('CVU');
          if (opts.sqlcl) parts.push('SQLCL');
        }
        if (opts.specificPatches && opts.specificPatches.trim()) parts.push(opts.specificPatches.trim());
        return parts.join(',');
      };
      const defaultSid = `DB${patchNum}`;
      const isUpgrade = operationType === 'upgrade';
      const tv = patch.targetVersion;
      const isV23or26 = tv === '23' || tv === '26';
      const defaultSourceHome = isUpgrade
        ? '/u01/app/oracle/product/12.2.0.1/dbhome_1'
        : isV23or26 ? `/u01/app/oracle/product/${getSourceVersion(versionConfig, '23')}/dbhome_1`
        : tv === '21' ? '/u01/app/oracle/product/21.3/dbhome_1'
        : '/u01/app/oracle/product/19.15/dbhome_1';
      const defaultTargetHome = isV23or26 ? `/u01/app/oracle/product/${getTargetVersion(versionConfig, '23')}/dbhome_1`
        : tv === '21' ? `/u01/app/oracle/product/${getTargetVersion(versionConfig, '21')}/dbhome_1`
        : `/u01/app/oracle/product/${getTargetVersion(versionConfig, '19')}/dbhome_1`;
      text += `${prefix}.sid=${patch.sid || defaultSid}\n`;
      // Add log_dir immediately after sid (path: .../autoupgrade/log/<...>)
      if (!isUpgrade && ['analyze', 'fixups', 'deploy'].includes(executionMode)) {
        const isWindows = patch.platform === 'WINDOWS.X64';
        const sidValue = patch.sid || defaultSid;
        const logDirValue = patch.logDir || (isWindows ? `C:\\Oracle\\AutoUpgrade\\Log\\${sidValue}` : `/home/oracle/autoupgrade/log/${sidValue}`);
        text += `${prefix}.log_dir=${logDirValue}\n`;
      }
      if (isUpgrade) {
        const sidValue = patch.sid || defaultSid;
        const isWindows = patch.platform === 'WINDOWS.X64';
        const logDirValue = patch.logDir || (isWindows 
          ? `C:\\Oracle\\AutoUpgrade\\Log\\${sidValue}_Upgrade`
          : `/home/oracle/autoupgrade/log/${sidValue}_Upgrade`);
        text += `${prefix}.log_dir=${logDirValue}\n`;
        const winSourceHome = isUpgrade
          ? 'C:\\Oracle\\Product\\12.2.0.1\\dbhome_1'
          : isV23or26 ? `C:\\Oracle\\Product\\${getSourceVersion(versionConfig, '23')}\\dbhome_1`
          : tv === '21' ? 'C:\\Oracle\\Product\\21.3\\dbhome_1'
          : 'C:\\Oracle\\Product\\19.15\\dbhome_1';
        const winTargetHome = isV23or26 ? `C:\\Oracle\\Product\\${getTargetVersion(versionConfig, '23')}\\dbhome_1`
          : tv === '21' ? `C:\\Oracle\\Product\\${getTargetVersion(versionConfig, '21')}\\dbhome_1`
          : `C:\\Oracle\\Product\\${getTargetVersion(versionConfig, '19')}\\dbhome_1`;
        text += `${prefix}.source_home=${isWindows ? (patch.sourceHome || winSourceHome) : (patch.sourceHome || defaultSourceHome)}\n`;
        text += `${prefix}.target_home=${isWindows ? (patch.targetHome || winTargetHome) : (patch.targetHome || defaultTargetHome)}\n`;
        if (isWindows) {
          text += `${prefix}.wincredential=${patch.winCredential || 'C:\\Oracle\\AutoUpgrade\\Credential'}\n`;
          if (patch.deleteWinCredentialFile === false) {
            text += `${prefix}.delete_wincredential_file=NO\n`;
          }
        }
        // Start Time
        if (patch.startTimeType === 'delay' && patch.startTimeValue) {
          text += `${prefix}.start_time=${patch.startTimeValue}\n`;
        } else if (patch.startTimeType === 'datetime' && patch.startTimeValue && !isDateInPast(patch.startTimeValue)) {
          text += `${prefix}.start_time=${formatDateTimeForConfig(patch.startTimeValue)}\n`;
        }
        if (patch.upgradeType === 'regular') {
          if (patch.proactiveFixups) {
            if (patch.distributedUpgrade) {
              text += `${prefix}.tune_setting=proactive_fixups=true,distributed_upgrade=yes\n`;
            } else {
              text += `${prefix}.tune_setting=proactive_fixups=true\n`;
            }
          } else if (patch.distributedUpgrade) {
            text += `${prefix}.tune_setting=distributed_upgrade=yes\n`;
          }
          if (patch.upgradeNodeEnabled) {
            const nodeValue = patch.upgradeNodeValue && patch.upgradeNodeValue.trim() !== '' ? patch.upgradeNodeValue : 'myhost01';
            text += `${prefix}.upgrade_node=${nodeValue}\n`;
          }
          text += `${prefix}.restoration=${patch.restoration ? 'YES' : 'NO'}\n`;
          if (patch.restoration && patch.dropGrpAfterPatching) {
            text += `${prefix}.drop_grp_after_patching=YES\n`;
          }
          // OEM grouped parameters (only for regular upgrade)
          const oemLines = [];
          if (patch.emTargetNameEnabled) {
            const emTargetNameValue = patch.emTargetName && patch.emTargetName.trim() !== ''
              ? patch.emTargetName
              : (patch.sid || `DB${patchNum}`);
            oemLines.push(`${prefix}.em_target_name=${emTargetNameValue}`);
          }
          if (patch.emcliPathEnabled) {
            const emcliPathValue = patch.emcliPath && patch.emcliPath.trim() !== ''
              ? patch.emcliPath
              : (patch.platform === 'WINDOWS.X64'
                  ? 'C:\\Oracle\\Product\\Agent241\\24.1.0.0.0\\bin'
                  : '/u01/app/oracle/product/agent241/24.1.0.0.0/bin');
            oemLines.push(`${prefix}.emcli_path=${emcliPathValue}`);
          }
          if (patch.emBlackoutSuffixEnabled) {
            const emBlackoutSuffixValue = patch.emBlackoutSuffix && patch.emBlackoutSuffix.trim() !== ''
              ? patch.emBlackoutSuffix
              : `em_blackout_AutoUpgrade_${patch.sid || `DB${patchNum}`}`;
            oemLines.push(`${prefix}.em_blackout_suffix=${emBlackoutSuffixValue}`);
          }
          if (oemLines.length > 0) {
            text += oemLines.join('\n') + '\n';
          }
          if (patch.runUtlrpUpg === false) {
            text += `${prefix}.run_utlrp=NO\n`;
          }
          // Add PFILE configurations only if enabled
          if (patch.delDuringUpgradePfileEnabled) {
            const value = patch.delDuringUpgradePfile && patch.delDuringUpgradePfile.trim() !== ''
              ? patch.delDuringUpgradePfile
              : (isAnyWindows
                  ? `C:\\Oracle\\AutoUpgrade\\${patch.sid || 'DB1'}_del_during.ora`
                  : `/home/oracle/autoupgrade/${patch.sid || 'DB1'}_del_during.ora`);
            text += `${prefix}.del_during_upgrade_pfile=${value}\n`;
          }
          if (patch.delAfterUpgradePfileEnabled) {
            const value = patch.delAfterUpgradePfile && patch.delAfterUpgradePfile.trim() !== ''
              ? patch.delAfterUpgradePfile
              : (isAnyWindows
                  ? `C:\\Oracle\\AutoUpgrade\\${patch.sid || 'DB1'}_del_after.ora`
                  : `/home/oracle/autoupgrade/${patch.sid || 'DB1'}_del_after.ora`);
            text += `${prefix}.del_after_upgrade_pfile=${value}\n`;
          }
          if (patch.addDuringUpgradePfileEnabled) {
            const value = patch.addDuringUpgradePfile && patch.addDuringUpgradePfile.trim() !== ''
              ? patch.addDuringUpgradePfile
              : (isAnyWindows
                  ? `C:\\Oracle\\AutoUpgrade\\${patch.sid || 'DB1'}_add_during.ora`
                  : `/home/oracle/autoupgrade/${patch.sid || 'DB1'}_add_during.ora`);
            text += `${prefix}.add_during_upgrade_pfile=${value}\n`;
          }
          if (patch.addAfterUpgradePfileEnabled) {
            const value = patch.addAfterUpgradePfile && patch.addAfterUpgradePfile.trim() !== ''
              ? patch.addAfterUpgradePfile
              : (isAnyWindows
                  ? `C:\\Oracle\\AutoUpgrade\\${patch.sid || 'DB1'}_add_after.ora`
                  : `/home/oracle/autoupgrade/${patch.sid || 'DB1'}_add_after.ora`);
            text += `${prefix}.add_after_upgrade_pfile=${value}\n`;
          }
        }
        if (patch.upgradeType === 'noncdb2cdb') {
          const targetCdb = patch.targetCdbSid && patch.targetCdbSid.trim() !== '' ? patch.targetCdbSid : 'CDB19';
          const targetPdb = patch.newPdbName && patch.newPdbName.trim() !== '' ? patch.newPdbName : 'PDB19';
          const sourcePath = patch.sourcePath && patch.sourcePath.trim() !== '' ? patch.sourcePath : (patch.sid || defaultSid);
          const targetPath = patch.targetPath && patch.targetPath.trim() !== ''
            ? patch.targetPath
            : (isWindows
                ? `${targetCdb}\\${targetPdb}`
                : `${targetCdb}/${targetPdb}`);
          text += `${prefix}.target_cdb=${targetCdb}\n`;
          text += `${prefix}.target_pdb_name=${targetPdb}\n`;
          const copyOption = patch.fileNameConvertOption || 'convert';
          if (copyOption === 'convert') {
            text += `${prefix}.target_pdb_copy_option=file_name_convert=('${sourcePath}', '${targetPath}')\n`;
          } else if (copyOption === 'none') {
            text += `${prefix}.target_pdb_copy_option=file_name_convert=NONE\n`;
          }
        }
        if (patch.upgradeType === 'pdbrelocation') {
          // Source PDB
          const sourcePdb = patch.sourcePdb && patch.sourcePdb.trim() !== '' ? patch.sourcePdb : 'pdb1_src';
          text += `${prefix}.pdbs=${sourcePdb}\n`;
          // Target CDB
          const targetCdb = patch.targetCdbSid && patch.targetCdbSid.trim() !== '' ? patch.targetCdbSid : 'cdb19_tgt';
          text += `${prefix}.target_cdb=${targetCdb}\n`;
          // Target PDB Name
          const targetPdbName = patch.targetPdbName && patch.targetPdbName.trim() !== '' ? patch.targetPdbName : 'pdb1_tgt';
          text += `${prefix}.target_pdb_name.${sourcePdb}=${targetPdbName}\n`;
          // DB Link & Refresh Option
          const dbLink = patch.dbLink && patch.dbLink.trim() !== '' ? patch.dbLink : 'db19_link';
          let refreshOption = '';
          if (patch.refreshOption === 'interval') {
            const mins = Number(patch.refreshInterval);
            const seconds = !isNaN(mins) && mins > 0 ? mins * 60 : 1200; // default 20 min
            refreshOption = `${seconds}`;
          } else {
            refreshOption = 'CLONE_ONLY';
          }
          text += `${prefix}.source_dblink.${sourcePdb}=${dbLink} ${refreshOption}\n`;
          // Parallel PDB Creation
          const parallelPdb = Number(patch.parallelPdbCreation);
          if (!isNaN(parallelPdb) && parallelPdb > 0) {
            text += `${prefix}.parallel_pdb_creation_clause.${sourcePdb}=${parallelPdb}\n`;
          }
          // Drop DB Link
          if (patch.dropDbLink) {
            text += `${prefix}.drop_dblink=yes\n`;
          }
          // File Name Convert / Copy Option
          const copyOption = patch.fileNameConvertOption || 'convert';
          const isWindows = patch.platform === 'WINDOWS.X64';
          if (copyOption === 'convert') {
            const sourcePath = patch.sourcePath && patch.sourcePath.trim() !== ''
              ? patch.sourcePath
              : (isWindows 
                  ? `${(patch.sid || 'DB1').toUpperCase()}\\${(sourcePdb || 'PDB1_SRC').toUpperCase()}`
                  : `${patch.sid !== '' ? patch.sid : 'DB1'}/${sourcePdb || 'pdb1_src'}`);
            const targetPath = patch.targetPath && patch.targetPath.trim() !== ''
              ? patch.targetPath
              : (isWindows 
                  ? `${(targetCdb || 'CDB19_TGT').toUpperCase()}\\${(targetPdbName || 'PDB1_TGT').toUpperCase()}`
                  : `${targetCdb !== '' ? targetCdb : 'cdb19_tgt'}/${targetPdbName || 'pdb1_tgt'}`);
            text += `${prefix}.target_pdb_copy_option.${isWindows ? sourcePdb.toUpperCase() : sourcePdb}=file_name_convert=('${sourcePath}', '${targetPath}')\n`;
          } else if (copyOption === 'none') {
            text += `${prefix}.target_pdb_copy_option.${isWindows ? sourcePdb.toUpperCase() : sourcePdb}=file_name_convert=NONE\n`;
          }
          // If nocopy, do not output anything
        }
        if (patch.upgradeType === 'unplugplug') {
          const sourcePdb = patch.sourcePdb && patch.sourcePdb.trim() !== '' ? patch.sourcePdb : 'pdb1_src';
          text += `${prefix}.pdbs=${sourcePdb}\n`;
          const targetCdb = patch.targetCdbSid && patch.targetCdbSid.trim() !== '' ? patch.targetCdbSid : 'cdb19_tgt';
          text += `${prefix}.target_cdb=${targetCdb}\n`;
          // File Name Convert / Copy Option (default: NONE)
          const copyOption = patch.fileNameConvertOption || 'none';
          const isWinUp = patch.platform === 'WINDOWS.X64';
          if (copyOption === 'convert') {
            const srcPath = patch.sourcePath && patch.sourcePath.trim() !== ''
              ? patch.sourcePath
              : (isWinUp
                  ? `${(patch.sid || 'DB1').toUpperCase()}\\${sourcePdb.toUpperCase()}`
                  : `${patch.sid || 'DB1'}/${sourcePdb}`);
            const tgtPath = patch.targetPath && patch.targetPath.trim() !== ''
              ? patch.targetPath
              : (isWinUp
                  ? `${targetCdb.toUpperCase()}\\${sourcePdb.toUpperCase()}`
                  : `${targetCdb}/${sourcePdb}`);
            text += `${prefix}.target_pdb_copy_option.${isWinUp ? sourcePdb.toUpperCase() : sourcePdb}=file_name_convert=('${srcPath}', '${tgtPath}')\n`;
          } else if (copyOption === 'none') {
            text += `${prefix}.target_pdb_copy_option.${isWinUp ? sourcePdb.toUpperCase() : sourcePdb}=file_name_convert=NONE\n`;
          }
          // manage_standbys_clause — omit only when empty (Deferrable Recovery / switch OFF)
          if (patch.manageStandbysClause) {
            text += `${prefix}.manage_standbys_clause=${patch.manageStandbysClause}\n`;
          }
        }
        if (patch.restoration && patch.dropGrpAfterPatching) {
          text += `${prefix}.drop_grp_after_patching=YES\n`;
        }
        if (patch.timezoneUpgUpg === false) {
          text += `${prefix}.timezone_upg=NO\n`;
        }
        if (patch.removeUnderscoreUpg === false) {
          text += `${prefix}.remove_underscore_parameters=NO\n`;
        }
        if (patch.raiseCompatibleUpg) {
          if (patch.raiseCompatibleUpgValue && patch.raiseCompatibleUpgValue.trim() !== '') {
            text += `${prefix}.raise_compatible=${patch.raiseCompatibleUpgValue.trim()}\n`;
          } else {
            text += `${prefix}.raise_compatible=YES\n`;
          }
        }
        if (patch.restoration && patch.dropGrpAfterUpgrade) {
          text += `${prefix}.drop_grp_after_upgrade=YES\n`;
        }
        // Always add source_tns_admin_dir if enabled
        if (patch.sourceTnsAdminEnabled) {
          const value = patch.sourceTnsAdminDir && patch.sourceTnsAdminDir.trim() !== ''
            ? patch.sourceTnsAdminDir
            : (isWindows ? 'C:\\Oracle\\Source\\TNS_ADMIN' : '/u01/app/source/tns_admin');
          text += `${prefix}.source_tns_admin_dir=${value}\n`;
        }
        // Always add target_tns_admin_dir if enabled
        if (patch.targetTnsAdminEnabled) {
          const value = patch.targetTnsAdminDir && patch.targetTnsAdminDir.trim() !== ''
            ? patch.targetTnsAdminDir
            : (isWindows ? 'C:\\Oracle\\Target\\TNS_ADMIN' : '/u02/app/target/tns_admin');
          text += `${prefix}.target_tns_admin_dir=${value}\n`;
        }
      } else {
        const isWindows = patch.platform === 'WINDOWS.X64';
        const winSourceHome = isUpgrade
          ? 'C:\\Oracle\\Product\\12.2.0.1\\dbhome_1'
          : isV23or26 ? `C:\\Oracle\\Product\\${getSourceVersion(versionConfig, '23')}\\dbhome_1`
          : tv === '21' ? 'C:\\Oracle\\Product\\21.3\\dbhome_1'
          : 'C:\\Oracle\\Product\\19.15\\dbhome_1';
        const winTargetHome = isV23or26 ? `C:\\Oracle\\Product\\${getTargetVersion(versionConfig, '23')}\\dbhome_1`
          : tv === '21' ? `C:\\Oracle\\Product\\${getTargetVersion(versionConfig, '21')}\\dbhome_1`
          : `C:\\Oracle\\Product\\${getTargetVersion(versionConfig, '19')}\\dbhome_1`;
        text += `${prefix}.source_home=${isWindows ? (patch.sourceHome || winSourceHome) : (patch.sourceHome || defaultSourceHome)}\n`;
        text += `${prefix}.target_home=${isWindows ? (patch.targetHome || winTargetHome) : (patch.targetHome || defaultTargetHome)}\n`;
        if (isWindows) {
          text += `${prefix}.wincredential=${patch.winCredential || 'C:\\Oracle\\AutoUpgrade\\Credential'}\n`;
          if (patch.deleteWinCredentialFile === false) {
            text += `${prefix}.delete_wincredential_file=NO\n`;
          }
        }
        if (!patch.existingOracleHome && patch.targetVersionEnabled) {
          text += `${prefix}.target_version=${patch.targetVersion || '19'}\n`;
        }
        if (!patch.existingOracleHome && patch.platform) {
          text += `${prefix}.platform=${patch.platform}\n`;
        }
        text += `${prefix}.restoration=${patch.restoration ? 'YES' : 'NO'}\n`;
        if (patch.restoration && patch.dropGrpAfterPatching) {
          text += `${prefix}.drop_grp_after_patching=YES\n`;
        }
        if (!patch.existingOracleHome) {
          text += `${prefix}.folder=${isWindows ? (patch.patchFolder || 'C:\\Oracle\\AutoUpgrade\\Patches') : (patch.patchFolder || '/home/oracle/autoupgrade/patches')}\n`;
          text += `${prefix}.patch=${getPatchConfig(patch.patchOptions)}\n`;
          const effectiveDownload = patch.patchOptions.useGoldImageFile ? 'NO' : patch.download;
          text += `${prefix}.download=${effectiveDownload}\n`;
        }
        // Gold Image (for deploy/analyze when download=YES — ALL is default, skip it)
        if ((executionMode === 'deploy' || executionMode === 'analyze') && patch.download === 'YES' && !patch.patchOptions.useGoldImageFile && patch.patchOptions.goldImage && patch.patchOptions.goldImage !== 'AUTO' && patch.patchOptions.goldImage !== 'ALL') {
          text += `${prefix}.gold_image=${patch.patchOptions.goldImage}\n`;
        }
        if ((executionMode === 'deploy' || executionMode === 'analyze') && patch.download === 'YES' && !patch.patchOptions.useGoldImageFile && (patch.patchOptions.goldImage === 'YES' || patch.patchOptions.goldImage === 'ALL') && patch.patchOptions.goldImageSecurityLevel && patch.patchOptions.goldImageSecurityLevel !== 'HIGH') {
          text += `${prefix}.gold_image.security_patch_level=${patch.patchOptions.goldImageSecurityLevel}\n`;
        }
        if (executionMode === 'deploy' && !patch.existingOracleHome && !patch.patchOptions.useGoldImageFile && patch.patchOptions.createGoldImage) {
          text += `${prefix}.create_gold_image=${patch.patchOptions.createGoldImage}\n`;
        }
        if (patch.sourceTnsAdminEnabled && (patch.sourceTnsAdminDir || patch.sourceTnsAdminDir === '')) {
          const value = patch.sourceTnsAdminDir && patch.sourceTnsAdminDir.trim() !== ''
            ? patch.sourceTnsAdminDir
            : (isWindows ? 'C:\\Oracle\\Source\\TNS_ADMIN' : '/u01/app/source/tns_admin');
          text += `${prefix}.source_tns_admin_dir=${value}\n`;
        }
        if (patch.targetTnsAdminEnabled && (patch.targetTnsAdminDir || patch.targetTnsAdminDir === '')) {
          const value = patch.targetTnsAdminDir && patch.targetTnsAdminDir.trim() !== ''
            ? patch.targetTnsAdminDir
            : (isWindows ? 'C:\\Oracle\\Target\\TNS_ADMIN' : '/u02/app/target/tns_admin');
          text += `${prefix}.target_tns_admin_dir=${value}\n`;
                  }
          // Add RAC Rolling and Drain Timeout for analyze and deploy modes only
          if (executionMode === 'analyze' || executionMode === 'deploy') {
            if (patch.racRolling && patch.racRolling !== 'AUTO') {
              text += `${prefix}.rac_rolling=${patch.racRolling}\n`;
            }
            if (patch.drainTimeout && patch.drainTimeout.trim() !== '') {
              const drainTimeoutValue = patch.drainTimeout === 'WAIT' ? 'WAIT' : patch.drainTimeout;
              text += `${prefix}.drain_timeout=${drainTimeoutValue}\n`;
            }
          }
          // Home Settings for analyze/fixups/deploy
          if (['analyze', 'fixups', 'deploy'].includes(executionMode)) {
            const isWindows = patch.platform === 'WINDOWS.X64';
            const homeSettingsLines: string[] = [];
            // account_type (Windows only)
            if (patch.hsAccountType && patch.hsAccountType.trim() && isWindows) {
              homeSettingsLines.push(`${prefix}.home_settings.account_type=${patch.hsAccountType.trim()}`);
            }
            // Binary Options: write =yes when explicitly ON, =no when explicitly OFF
            if (patch.binaryOptionRac === true) homeSettingsLines.push(`${prefix}.home_settings.binopt.rac=yes`);
            else if (patch.binaryOptionRac === false) homeSettingsLines.push(`${prefix}.home_settings.binopt.rac=no`);
            const writeBinoptLine = (val: boolean | undefined, key: string, winExclude?: boolean) => {
              if (winExclude && isWindows) return;
              if (val === true) homeSettingsLines.push(`${prefix}.home_settings.binopt.${key}=yes`);
              else if (val === false) homeSettingsLines.push(`${prefix}.home_settings.binopt.${key}=no`);
            };
            writeBinoptLine(patch.hsBinoptAsm, 'asm', true);
            writeBinoptLine(patch.hsBinoptDnfs, 'dnfs');
            writeBinoptLine(patch.hsBinoptDm, 'dm', true);
            writeBinoptLine(patch.hsBinoptJox, 'jox', true);
            writeBinoptLine(patch.hsBinoptOlap, 'olap');
            writeBinoptLine(patch.hsBinoptPart, 'part');
            writeBinoptLine(patch.hsBinoptRat, 'rat');
            writeBinoptLine(patch.hsBinoptSdo, 'sdo', true);
            writeBinoptLine(patch.hsBinoptUniaud, 'uniaud');
            // Cluster nodes
            if (patch.clusterNodesEnabled) {
              const clusterNodesValue = patch.clusterNodes && patch.clusterNodes.trim() !== ''
                ? patch.clusterNodes
                : 'node1,node2,node3';
              homeSettingsLines.push(`${prefix}.home_settings.cluster_nodes=${clusterNodesValue}`);
            }
            // Edition (moved from BasicInformation - now available in all modes via Home Settings)
            if (patch.editionEnabled && patch.edition) {
              homeSettingsLines.push(`${prefix}.home_settings.edition=${patch.edition}`);
            }
            // Home name (Linux only)
            if (patch.hsHomeName && patch.hsHomeName.trim() && !isWindows) {
              homeSettingsLines.push(`${prefix}.home_settings.home_name=${patch.hsHomeName.trim()}`);
            }
            // ignore_opatch_conflict (only if not default 'error')
            if (patch.hsIgnoreOpatchConflict && patch.hsIgnoreOpatchConflict !== 'error') {
              homeSettingsLines.push(`${prefix}.home_settings.ignore_opatch_conflict=${patch.hsIgnoreOpatchConflict}`);
            }
            // ignore_prereq_failure
            if (patch.ignorePrereqFailure) {
              homeSettingsLines.push(`${prefix}.home_settings.ignore_prereq_failure=YES`);
            }
            // inventory_group (Linux only)
            if (patch.inventoryGroupEnabled && !isWindows) {
              const inventoryGroupValue = patch.inventoryGroup && patch.inventoryGroup.trim() !== ''
                ? patch.inventoryGroup
                : 'oinstall';
              homeSettingsLines.push(`${prefix}.home_settings.inventory_group=${inventoryGroupValue}`);
            }
            // inventory_location (Linux only)
            if (patch.oraInventoryEnabled && !isWindows) {
              const oraInventoryValue = patch.oraInventory && patch.oraInventory.trim() !== ''
                ? patch.oraInventory
                : '/u01/app/oraInventory';
              homeSettingsLines.push(`${prefix}.home_settings.inventory_location=${oraInventoryValue}`);
            }
            // oracle_base (moved from BasicInformation - now available in all modes via Home Settings)
            if (patch.oracleBaseEnabled && patch.oracleBase) {
              homeSettingsLines.push(`${prefix}.home_settings.oracle_base=${patch.oracleBase}`);
            }
            // OS groups (Linux only, only if user provided value)
            if (patch.hsOsdbaGroup && patch.hsOsdbaGroup.trim() && !isWindows) homeSettingsLines.push(`${prefix}.home_settings.osdba_group=${patch.hsOsdbaGroup.trim()}`);
            if (patch.hsOsoperGroup && patch.hsOsoperGroup.trim() && !isWindows) homeSettingsLines.push(`${prefix}.home_settings.osoper_group=${patch.hsOsoperGroup.trim()}`);
            if (patch.hsOsbackupdbaGroup && patch.hsOsbackupdbaGroup.trim() && !isWindows) homeSettingsLines.push(`${prefix}.home_settings.osbackupdba_group=${patch.hsOsbackupdbaGroup.trim()}`);
            if (patch.hsOsdgdbaGroup && patch.hsOsdgdbaGroup.trim() && !isWindows) homeSettingsLines.push(`${prefix}.home_settings.osdgdba_group=${patch.hsOsdgdbaGroup.trim()}`);
            if (patch.hsOskmdbaGroup && patch.hsOskmdbaGroup.trim() && !isWindows) homeSettingsLines.push(`${prefix}.home_settings.oskmdba_group=${patch.hsOskmdbaGroup.trim()}`);
            if (patch.hsOsracdbaGroup && patch.hsOsracdbaGroup.trim() && !isWindows) homeSettingsLines.push(`${prefix}.home_settings.osracdba_group=${patch.hsOsracdbaGroup.trim()}`);
            // read_only (only if yes)
            if (patch.hsReadOnly) homeSettingsLines.push(`${prefix}.home_settings.read_only=yes`);
            // ru_apply: only write if explicitly YES and RAC is not ON (RAC=ON makes ru_apply=yes implicit)
            if (patch.hsRuApply && patch.binaryOptionRac !== true) homeSettingsLines.push(`${prefix}.home_settings.ru_apply=yes`);
            if (homeSettingsLines.length > 0) {
              text += homeSettingsLines.join('\n') + '\n';
            }
            // env=TMP (separate format)
            if (patch.hsEnvTmp && patch.hsEnvTmp.trim()) {
              text += `${prefix}.env=TMP=${patch.hsEnvTmp.trim()}\n`;
            }
          }
        // ADD START TIME LOGIC FOR PATCH HERE
        if (patch.startTimeType === 'delay' && patch.startTimeValue) {
          text += `${prefix}.start_time=${patch.startTimeValue}\n`;
        } else if (patch.startTimeType === 'datetime' && patch.startTimeValue && !isDateInPast(patch.startTimeValue)) {
          text += `${prefix}.start_time=${formatDateTimeForConfig(patch.startTimeValue)}\n`;
        }
      }
      // Add proxy if needed (patch only)
      if (!isUpgrade && patch.download === 'YES' && patch.useProxy) {
        text += `${prefix}.proxy_host=${patch.proxyHost || 'proxy.mycompany.com'}\n`;
        text += `${prefix}.proxy_port=${patch.proxyPort || '80'}\n`;
      }
      // Pass-through: valid params from loaded config that have no UI field
      if (patch.passthroughParams && Object.keys(patch.passthroughParams).length > 0) {
        for (const [key, val] of Object.entries(patch.passthroughParams)) {
          text += `${prefix}.${key}=${val}\n`;
        }
      }
              text += `\n`;
      });
      setConfigText(text);
  }, [patches, operationType, globalLogDir, keystorePath, executionMode, dbTabs, versionConfig]);

  const handleConfigLoaded = (data: {
    globalLogDir: string;
    keystorePath: string;
    patches: PatchType[];
    operationType?: string;
    executionMode?: string;
    tabList?: { key: string; label: string }[];
  }) => {
    if (data.operationType && data.operationType !== operationType) {
      lastLoadedConfig.current = data;
      setOperationType(data.operationType as 'patch' | 'upgrade');
      // The rest of the state will be set by the useEffect that listens to operationType
      return;
    }
    setGlobalLogDir(data.globalLogDir);
    setKeystorePath(data.keystorePath);
    // If loading from config (tabList present), use loaded data directly
    if (data.tabList && data.tabList.length === data.patches.length) {
      setDbTabs(data.tabList);
      setPatches(data.patches);
      setActiveDbTab(data.tabList[0].key);
      const newActiveSubTabs = data.tabList.reduce((acc: { [key: string]: number }, tab: { key: string }) => {
        acc[tab.key] = 0;
        return acc;
      }, {} as { [key: string]: number });
      setActiveSubTabs(newActiveSubTabs);
    } else {
      // fallback to old logic for new/empty configs or user actions
      const mappedPatches = data.patches.map((patch: PatchType) => {
        let upgradeType = patch.upgradeType;
        if (!upgradeType) {
          if (patch.sourcePdb) {
            upgradeType = 'pdbrelocation';
          } else if (patch.targetCdbSid && (patch.newPdbName || patch.targetPdbName)) {
            upgradeType = 'noncdb2cdb';
          } else {
            upgradeType = 'regular';
          }
        }
        let fileNameConvertOption = patch.fileNameConvertOption;
        if (fileNameConvertOption === undefined || fileNameConvertOption === '') {
          if (patch.sourcePath && patch.targetPath) {
            fileNameConvertOption = 'convert';
          } else {
            fileNameConvertOption = 'nocopy';
          }
        }
        let refreshOption = patch.refreshOption;
        if (upgradeType === 'pdbrelocation' && !refreshOption) {
          refreshOption = 'clone_only';
        }
        return {
          ...patch,
          upgradeType,
          fileNameConvertOption,
          refreshOption,
          delDuringUpgradePfileEnabled: patch.delDuringUpgradePfileEnabled ?? false,
          addAfterUpgradePfileEnabled: patch.addAfterUpgradePfileEnabled ?? false,
          delAfterUpgradePfile: patch.delAfterUpgradePfile ?? '',
          delAfterUpgradePfileEnabled: patch.delAfterUpgradePfileEnabled ?? false,
          addDuringUpgradePfile: patch.addDuringUpgradePfile ?? '',
          addDuringUpgradePfileEnabled: patch.addDuringUpgradePfileEnabled ?? false,
        };
      });
      const prefix = operationType === 'upgrade' ? 'upg' : 'patch';
      const newTabs = mappedPatches.map((_: PatchType, idx: number) => ({
        key: `${prefix}${idx + 1}`,
        label: `${prefix}${idx + 1}`,
      }));
      setDbTabs(newTabs);
      setActiveDbTab(newTabs[0].key);
      const newActiveSubTabs = newTabs.reduce((acc: { [key: string]: number }, tab: { key: string }) => {
        acc[tab.key] = 0;
        return acc;
      }, {} as { [key: string]: number });
      setActiveSubTabs(newActiveSubTabs);
      setPatches(mappedPatches);
    }
    if (data.executionMode && (!data.operationType || data.operationType === 'patch')) {
      setExecutionMode(data.executionMode as 'download' | 'create_home' | 'upgrade');
    }
  };

  // Show keystore in TDE if needed for current patch
  const showTdeKeystore = (currentPatch.download === 'NO' && currentPatch.tdeEnabled) || (currentPatch.download === 'YES' && currentPatch.tdeEnabled);

  // Reset subtab index to 0 when executionMode or activeDbTab changes (prevents invalid tab index)
  React.useEffect(() => {
    setActiveSubTabs(prev => ({
      ...prev,
      [activeDbTab]: 0
    }));
  }, [executionMode, activeDbTab]);

  const [copied, setCopied] = React.useState(false);
  const [blink, setBlink] = React.useState(false);

  const [editingTab, setEditingTab] = React.useState<string | null>(null);
  const [editingLabel, setEditingLabel] = React.useState('');

  const handleEditTab = (key: string, label: string) => {
    setEditingTab(key);
    setEditingLabel(label);
  };
  const handleEditTabChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingLabel(e.target.value);
  };
  const handleEditTabBlur = (key: string) => {
    setDbTabs(tabs => tabs.map(tab => tab.key === key ? { ...tab, label: editingLabel.trim() || tab.label } : tab));
    setEditingTab(null);
  };
  const handleEditTabKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, key: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditTabBlur(key);
    }
  };

  const [changelogOpen, setChangelogOpen] = React.useState(false);

  // Open changelog modal if URL hash is #changelog
  React.useEffect(() => {
    if (window.location.hash === '#changelog') {
      setChangelogOpen(true);
    }
  }, []);

  // Remove #changelog from URL when modal closes
  const handleCloseChangelog = () => {
    setChangelogOpen(false);
    if (window.location.hash === '#changelog') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  };

  React.useEffect(() => {
    setDbTabs([{ key: `${operationType === 'upgrade' ? 'upg1' : 'patch1'}`, label: `${operationType === 'upgrade' ? 'upg1' : 'patch1'}` }]);
    if (operationType === 'upgrade') {
      setPatches([{ ...defaultPatch(1, true), upgradeType: 'regular' }]);
    } else {
      setPatches([defaultPatch(1)]);
    }
    setActiveDbTab(`${operationType === 'upgrade' ? 'upg1' : 'patch1'}`);
    const firstKey = `${operationType === 'upgrade' ? 'upg1' : 'patch1'}`;
    setActiveSubTabs({ [firstKey]: 0 });
    setGlobalLogDir('');
  }, [operationType]);

  // --- Place this useEffect at the top level, not inside any function/component ---
  React.useEffect(() => {
    if (lastLoadedConfig.current) {
      const data = lastLoadedConfig.current;
      if (data.tabList && data.tabList.length === data.patches.length) {
        setDbTabs(data.tabList);
        setPatches(data.patches);
        setActiveDbTab(data.tabList[0].key);
        const newActiveSubTabs = data.tabList.reduce((acc: { [key: string]: number }, tab: { key: string }) => {
          acc[tab.key] = 0;
          return acc;
        }, {} as { [key: string]: number });
        setActiveSubTabs(newActiveSubTabs);
      }
      if (data.globalLogDir) setGlobalLogDir(data.globalLogDir);
      if (data.keystorePath) setKeystorePath(data.keystorePath);
      if (data.executionMode && (!data.operationType || data.operationType === 'patch')) {
        setExecutionMode(data.executionMode as 'download' | 'create_home' | 'upgrade');
      }
      lastLoadedConfig.current = null;
    }
  }, [operationType]);

  // In the component, use a ref and state to set leftColHeight dynamically
  const leftColRef = React.useRef<HTMLDivElement>(null);
  const [leftColHeight, setLeftColHeight] = React.useState('100%');
  React.useEffect(() => {
    if (leftColRef.current) {
      setLeftColHeight((leftColRef.current as HTMLDivElement).offsetHeight + 'px');
    }
  }, [patches, operationType, executionMode, copied, blink, changelogOpen]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ background: theme.palette.background.default, minHeight: '100vh', borderRadius: 0 }}>
        <Box sx={{ position: 'sticky', top: 0, zIndex: 1200, background: theme.palette.primary.main, borderRadius: 0, overflow: 'visible' }}>
          <Header toggleTheme={toggleTheme} themeMode={themeMode} />
        </Box>
        <Box sx={{ px: { xs: 2, md: 6 }, pt: 0.5, display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'flex-start', width: '100%', minHeight: '100vh', position: 'relative' }}>
          {/* Left: All main content except Generated Config */}
          <Box ref={leftColRef} sx={{ flex: '0 1 50%', maxWidth: { xs: '100%', lg: '50%' }, minWidth: 320, pr: { lg: '4%' }, width: '100%' }}>
            <Box sx={{ mb: 4 }}>
              <LoadConfigFile onConfigLoaded={handleConfigLoaded} />
            </Box>
            <Paper elevation={6} sx={{ ...cardSx, ...sectionBorderSx, background: theme.palette.background.paper }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, width: '100%' }}>
                  <IconButton size="small" onClick={() => setGlobalOptionsOpen(o => !o)} sx={{ mr: 1 }}>
                    {globalOptionsOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                  </IconButton>
                  <SettingsIcon sx={{ fontSize: 24, color: accentColor, mr: 1 }} />
                  <Typography sx={{ ...sectionHeaderSx, mb: 0, flex: 1 }}>Global Options</Typography>
                </Box>
                <Divider sx={{ mb: 3, borderBottomWidth: 2, width: '100%', borderColor: accentColor }} />
                {globalOptionsOpen && (
                  <Box sx={{ width: '100%', maxWidth: 900 }}>
                    <GlobalOptions
                      logDir={globalLogDir}
                      setLogDir={setGlobalLogDir}
                      placeholder={
                        patches.some(p => p.platform === 'WINDOWS.X64')
                          ? (operationType === 'upgrade' ? BASIC_PLACEHOLDERS.win_upgrade_log_dir : BASIC_PLACEHOLDERS.win_log_dir)
                          : (operationType === 'upgrade' ? '/home/oracle/autoupgrade/log' : '/home/oracle/autoupgrade/log')
                      }
                      operationType={operationType}
                      setOperationType={(v) => {
                        setOperationType(v as 'patch' | 'upgrade');
                        if (v === 'upgrade') {
                          setExecutionMode('deploy');
                          setValidationMessages([]); // Limpa mensagens ao mudar para upgrade
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Paper>
            <Paper elevation={6} sx={{ ...cardSx, ...sectionBorderSx, background: theme.palette.mode === 'dark' ? '#23272f' : '#f4f6fa' }}>
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, width: '100%' }}>
                  <IconButton size="small" onClick={() => setDbConfigsOpen(o => !o)} sx={{ mr: 1 }}>
                    {dbConfigsOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                  </IconButton>
                  <TableRowsOutlinedIcon sx={{ color: accentColor, fontSize: 24, mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: accentColor, fontSize: '1rem', flex: 1 }}>
                    Database Configurations
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3, borderBottomWidth: 2, width: '100%', borderColor: accentColor }} />
                {dbConfigsOpen && (
                  <Box>
                    {/* ...Database Configurations content unchanged... */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Tabs
                        value={activeDbTab}
                        onChange={handleChangeDbTab}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                          minHeight: 48,
                          background: theme.palette.mode === 'dark' ? '#31343a' : theme.palette.background.paper,
                          borderRadius: 2,
                          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)',
                          px: 1,
                          mb: 1,
                          '.MuiTab-root': {
                            minHeight: 40,
                            minWidth: 120,
                            borderRadius: 2,
                            mx: 0.5,
                            fontWeight: 500,
                            fontSize: 16,
                            color: theme.palette.text.secondary,
                            transition: 'background 0.2s, color 0.2s',
                            textTransform: 'none',
                            '&:hover': {
                              background: theme.palette.action.hover,
                              color: theme.palette.mode === 'light' ? theme.palette.primary.main : '#fff',
                            },
                          },
                          '.MuiTab-root.Mui-selected, .MuiTab-root.Mui-selected .MuiTab-wrapper': {
                            background: theme.palette.mode === 'light' ? '#003366' : theme.palette.primary.main,
                            color: theme.palette.mode === 'dark' ? '#1a1a1a !important' : '#fff !important',
                            fontWeight: 700,
                            textTransform: 'none',
                            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)',
                            transition: 'background 0.2s, color 0.2s',
                          },
                          '.MuiTab-root.Mui-selected:hover, .MuiTab-root.Mui-selected:focus': {
                            background: theme.palette.mode === 'light'
                              ? theme.palette.action.selected
                              : '#b2ebf2',
                            color: theme.palette.mode === 'light'
                              ? `${theme.palette.primary.main} !important`
                              : '#23272f !important',
                            transition: 'background 0.2s, color 0.2s',
                          },
                          '.MuiTabs-indicator': {
                            display: 'none',
                          },
                        }}
                      >
                        {dbTabs.map((tab) => (
                          <Tab
                            key={tab.key}
                            value={tab.key}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {editingTab === tab.key ? (
                                  <input
                                    value={editingLabel}
                                    autoFocus
                                    onChange={handleEditTabChange}
                                    onBlur={() => handleEditTabBlur(tab.key)}
                                    onKeyDown={e => handleEditTabKeyDown(e, tab.key)}
                                    style={{ fontSize: 15, width: 80, marginRight: 4 }}
                                  />
                                ) : (
                                  <>
                                    <span>{tab.label}</span>
                                    <EditIcon
                                      fontSize="small"
                                      sx={{ ml: 0.5, cursor: 'pointer', color: '#888' }}
                                      onClick={e => {
                                        e.stopPropagation();
                                        handleEditTab(tab.key, tab.label);
                                      }}
                                    />
                                  </>
                                )}
                                {dbTabs.length > 1 && (
                                  <span
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleRemoveDbTab(tab.key);
                                    }}
                                    style={{
                                      marginLeft: 8,
                                      cursor: 'pointer',
                                      color: theme.palette.grey[500],
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                    onMouseOver={e => (e.currentTarget.style.color = theme.palette.error.main)}
                                    onMouseOut={e => (e.currentTarget.style.color = theme.palette.grey[500])}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </span>
                                )}
                              </Box>
                            }
                            sx={{}}
                          />
                        ))}
                        <Tab
                          icon={
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                background: theme.palette.action.hover,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)',
                                transition: 'background 0.2s',
                                '&:hover': { background: theme.palette.primary.light },
                              }}
                            >
                              <AddIcon />
                            </Box>
                          }
                          aria-label="Add"
                          sx={{ minWidth: 40, minHeight: 40, mx: 0.5 }}
                          onClick={handleAddDbTab}
                          disabled={dbTabs.length >= 10}
                        />
                      </Tabs>
                    </Box>
                    <Divider sx={{ mb: 2, borderBottomWidth: 2, width: '100%' }} />
                    <Box sx={{ mt: 2, mb: 4 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 18,
                          color: accentColor,
                          mb: 1,
                          ml: 2,
                        }}
                      >
                        Execution Mode:
                      </Typography>
                      <Box sx={{ maxWidth: 400, width: '100%', ml: 2 }}>
                        <Select
                          value={executionMode}
                          onChange={e => setExecutionMode(e.target.value as 'download' | 'create_home' | 'upgrade')}
                          size="small"
                          fullWidth
                          displayEmpty
                          sx={{ fontSize: 15, color: executionMode ? theme.palette.text.primary : '#b0b0b0' }}
                        >
                          {filteredExecutionModes.map(mode => (
                            <MenuItem key={mode.value} value={mode.value}>
                              <Box>
                                <strong>{mode.label}</strong>
                                <Typography variant="body2" sx={{ color: '#888', fontSize: 13 }}>{mode.tooltip}</Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </Box>
                    {/* Render PatchSection or UpgradeSection based on operationType */}
                    {operationType === 'patch' ? (
                      <>
                        <Tabs
                          value={currentSubTab}
                          onChange={(_: any, newValue: number) => setActiveSubTabs(prev => ({ ...prev, [activeDbTab]: newValue }))}
                          sx={{
                            mb: 3,
                            minHeight: 44,
                            background: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.08)',
                            borderRadius: 3,
                            px: 1,
                            py: 0.5,
                            '.MuiTab-root': {
                              minHeight: 36,
                              minWidth: 120,
                              borderRadius: '18px', // Pill shape
                              mx: 0.5,
                              my: 0.5,
                              fontWeight: 500,
                              fontSize: 14,
                              color: theme.palette.mode === 'dark' ? accentColor : theme.palette.primary.main,
                              transition: 'all 0.3s cubic-bezier(.4,0,.2,1)',
                              textTransform: 'none',
                              background: 'transparent',
                              '&:hover': {
                                background: theme.palette.mode === 'dark' 
                                  ? 'rgba(0, 188, 212, 0.08)' 
                                  : 'rgba(0, 51, 102, 0.05)',
                                transform: 'translateY(-1px)',
                              },
                            },
                            '.Mui-selected': {
                              color: theme.palette.mode === 'dark' ? '#1a1a1a !important' : '#fff !important',
                              fontWeight: 600,
                              background: theme.palette.mode === 'dark' ? accentColor : theme.palette.primary.main,
                              borderRadius: '18px', // Pill shape
                              boxShadow: theme.palette.mode === 'dark'
                                ? '0 4px 12px 0 rgba(0, 188, 212, 0.3)'
                                : '0 4px 12px 0 rgba(0, 51, 102, 0.2)',
                              '&:hover': {
                                background: theme.palette.mode === 'dark' ? accentColor : theme.palette.primary.main,
                                transform: 'translateY(-1px)',
                              },
                            },
                            '.MuiTabs-indicator': {
                              display: 'none', // Hide the underline indicator for pill style
                            },
                          }}
                        >
                          {(executionMode === 'download'
                            ? ['Basic Information', 'Patch Content', 'Download Settings']
                            : executionMode === 'create_home'
                              ? ['Basic Information', 'Home Settings', 'Patch Content']
                              : ['Basic Information', 'Home Settings', 'Patch Content', 'Download Settings', 'Additional Options']
                          ).map((label: string, idx: number) => {
                            const disabledByExistingHome = currentPatch.existingOracleHome && (label === 'Home Settings' || label === 'Patch Content' || label === 'Download Settings');
                            const tab = (
                              <Tab
                                key={label}
                                label={label}
                                disabled={disabledByExistingHome}
                                sx={disabledByExistingHome ? { opacity: 0.35 } : {}}
                              />
                            );
                            return disabledByExistingHome ? (
                              <Tooltip
                                key={label}
                                title="Disabled — Existing Target Home mode does not download patches or configure a new home. Only Basic Information and Additional Options apply."
                                placement="top"
                                arrow
                                componentsProps={{ tooltip: { sx: { bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f', color: theme.palette.mode === 'dark' ? '#222' : '#fff', fontSize: 13, borderRadius: 2, boxShadow: 3, px: 2, py: 1, maxWidth: 320 } } }}
                              >
                                <span>{tab}</span>
                              </Tooltip>
                            ) : tab;
                          })}
                        </Tabs>
                        <Box sx={{ p: 2, background: theme.palette.background.paper, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                          {currentSubTab === 0 ? (
                            <BasicInformation
                              key={activeDbTab + (dbTabs[currentPatchIdx]?.label || '')}
                              sid={currentPatch.sid}
                              setSid={(v: string) => updateCurrentPatchField('sid', v)}
                              sourceHome={currentPatch.sourceHome}
                              setSourceHome={(v: string) => updateCurrentPatchField('sourceHome', v)}
                              targetHome={currentPatch.targetHome}
                              setTargetHome={(v: string) => updateCurrentPatchField('targetHome', v)}
                              platform={currentPatch.platform}
                              setPlatform={(v: string) => updateCurrentPatchField('platform', v)}
                              winCredential={currentPatch.winCredential || ''}
                              setWinCredential={(v: string) => updateCurrentPatchField('winCredential', v)}
                              deleteWinCredentialFile={currentPatch.deleteWinCredentialFile !== false}
                              setDeleteWinCredentialFile={(v: boolean) => updateCurrentPatchField('deleteWinCredentialFile', v)}
                              placeholders={{
                                sid: `DB${currentPatchIdx + 1}`,
                                source_home: BASIC_PLACEHOLDERS.source_home,
                                target_home: BASIC_PLACEHOLDERS.target_home,
                              }}
                              patchKey={activeDbTab}
                              patchLabel={dbTabs[currentPatchIdx]?.label || activeDbTab}
                              logDir={currentPatch.logDir || ''}
                              setLogDir={(v: string) => updateCurrentPatchField('logDir', v)}
                              executionMode={executionMode}
                              targetVersionEnabled={currentPatch.targetVersionEnabled}
                              setTargetVersionEnabled={(v: boolean) => updateCurrentPatchField('targetVersionEnabled', v)}
                              targetVersion={currentPatch.targetVersion}
                              setTargetVersion={(v: string) => updateCurrentPatchField('targetVersion', v)}
                              downloadMode={executionMode === 'download' || executionMode === 'create_home'}
                              keystorePath={keystorePath}
                              setKeystorePath={setKeystorePath}
                              sourceHomeEnabled={!!currentPatch.sourceHomeEnabled}
                              setSourceHomeEnabled={(v: boolean) => updateCurrentPatchField('sourceHomeEnabled', v)}
                              patchOptions={currentPatch.patchOptions}
                              oracleBaseEnabled={currentPatch.oracleBaseEnabled ?? false}
                              setOracleBaseEnabled={(v: boolean) => updateCurrentPatchField('oracleBaseEnabled', v)}
                              oracleBase={currentPatch.oracleBase ?? '/u01/app/oracle'}
                              setOracleBase={(v: string) => updateCurrentPatchField('oracleBase', v)}
                              editionEnabled={currentPatch.editionEnabled ?? false}
                              setEditionEnabled={(v: boolean) => updateCurrentPatchField('editionEnabled', v)}
                              edition={currentPatch.edition ?? 'ee'}
                              setEdition={(v: string) => updateCurrentPatchField('edition', v)}
                              renderValidationMessages={renderValidationMessages}
                              oraInventoryEnabled={currentPatch.oraInventoryEnabled ?? false}
                              setOraInventoryEnabled={(v: boolean) => updateCurrentPatchField('oraInventoryEnabled', v)}
                              oraInventory={currentPatch.oraInventory ?? ''}
                              setOraInventory={(v: string) => updateCurrentPatchField('oraInventory', v)}
                              inventoryGroupEnabled={currentPatch.inventoryGroupEnabled ?? false}
                              setInventoryGroupEnabled={(v: boolean) => updateCurrentPatchField('inventoryGroupEnabled', v)}
                              inventoryGroup={currentPatch.inventoryGroup ?? 'oinstall'}
                              setInventoryGroup={(v: string) => updateCurrentPatchField('inventoryGroup', v)}
                              ignorePrereqFailure={currentPatch.ignorePrereqFailure ?? false}
                              setIgnorePrereqFailure={(v: boolean) => updateCurrentPatchField('ignorePrereqFailure', v)}
                              versionConfig={versionConfig}
                              existingOracleHome={currentPatch.existingOracleHome ?? false}
                              setExistingOracleHome={(v: boolean) => updateCurrentPatchField('existingOracleHome', v)}
                            />
                          ) : currentSubTab === 1 && executionMode === 'download' ? (
                            <PatchContent
                              patchOptions={currentPatch.patchOptions}
                              setPatchOptions={updateCurrentPatchOptions}
                              patchFolder={currentPatch.patchFolder}
                              setPatchFolder={(v: string) => updateCurrentPatchField('patchFolder', v)}
                              patchFolderPlaceholder={
                                currentPatch.platform === 'WINDOWS.X64'
                                  ? 'C:\\Oracle\\AutoUpgrade\\Patches'
                                  : '/home/oracle/autoupgrade/patches'
                              }
                              executionMode={executionMode}
                              targetVersionEnabled={currentPatch.targetVersionEnabled}
                              targetVersion={currentPatch.targetVersion}
                              patchKey={activeDbTab}
                              platform={currentPatch.platform}
                              download={currentPatch.download}
                            />
                          ) : currentSubTab === 1 ? (
                            <HomeSettings
                              platform={currentPatch.platform}
                              executionMode={executionMode}
                              targetVersion={currentPatch.targetVersion}
                              oracleBaseEnabled={currentPatch.oracleBaseEnabled ?? false}
                              setOracleBaseEnabled={(v: boolean) => updateCurrentPatchField('oracleBaseEnabled', v)}
                              oracleBase={currentPatch.oracleBase ?? '/u01/app/oracle'}
                              setOracleBase={(v: string) => updateCurrentPatchField('oracleBase', v)}
                              editionEnabled={currentPatch.editionEnabled ?? false}
                              setEditionEnabled={(v: boolean) => updateCurrentPatchField('editionEnabled', v)}
                              edition={currentPatch.edition ?? 'ee'}
                              setEdition={(v: string) => updateCurrentPatchField('edition', v)}
                              oraInventoryEnabled={currentPatch.oraInventoryEnabled ?? false}
                              setOraInventoryEnabled={(v: boolean) => updateCurrentPatchField('oraInventoryEnabled', v)}
                              oraInventory={currentPatch.oraInventory ?? ''}
                              setOraInventory={(v: string) => updateCurrentPatchField('oraInventory', v)}
                              inventoryGroupEnabled={currentPatch.inventoryGroupEnabled ?? false}
                              setInventoryGroupEnabled={(v: boolean) => updateCurrentPatchField('inventoryGroupEnabled', v)}
                              inventoryGroup={currentPatch.inventoryGroup ?? 'oinstall'}
                              setInventoryGroup={(v: string) => updateCurrentPatchField('inventoryGroup', v)}
                              ignorePrereqFailure={currentPatch.ignorePrereqFailure ?? false}
                              setIgnorePrereqFailure={(v: boolean) => updateCurrentPatchField('ignorePrereqFailure', v)}
                              binaryOptionRac={currentPatch.binaryOptionRac}
                              setBinaryOptionRac={(v: boolean | undefined) => updateCurrentPatchField('binaryOptionRac', v)}
                              clusterNodesEnabled={!!currentPatch.clusterNodesEnabled}
                              setClusterNodesEnabled={(v: boolean) => updateCurrentPatchField('clusterNodesEnabled', v)}
                              clusterNodes={currentPatch.clusterNodes || ''}
                              setClusterNodes={(v: string) => updateCurrentPatchField('clusterNodes', v)}
                              hsAccountType={currentPatch.hsAccountType || ''}
                              setHsAccountType={(v: string) => updateCurrentPatchField('hsAccountType', v)}
                              hsBinoptAsm={currentPatch.hsBinoptAsm}
                              setHsBinoptAsm={(v: boolean | undefined) => updateCurrentPatchField('hsBinoptAsm', v)}
                              hsBinoptDnfs={currentPatch.hsBinoptDnfs}
                              setHsBinoptDnfs={(v: boolean | undefined) => updateCurrentPatchField('hsBinoptDnfs', v)}
                              hsBinoptDm={currentPatch.hsBinoptDm}
                              setHsBinoptDm={(v: boolean | undefined) => updateCurrentPatchField('hsBinoptDm', v)}
                              hsBinoptJox={currentPatch.hsBinoptJox}
                              setHsBinoptJox={(v: boolean | undefined) => updateCurrentPatchField('hsBinoptJox', v)}
                              hsBinoptOlap={currentPatch.hsBinoptOlap}
                              setHsBinoptOlap={(v: boolean | undefined) => updateCurrentPatchField('hsBinoptOlap', v)}
                              hsBinoptPart={currentPatch.hsBinoptPart}
                              setHsBinoptPart={(v: boolean | undefined) => updateCurrentPatchField('hsBinoptPart', v)}
                              hsBinoptRat={currentPatch.hsBinoptRat}
                              setHsBinoptRat={(v: boolean | undefined) => updateCurrentPatchField('hsBinoptRat', v)}
                              hsBinoptSdo={currentPatch.hsBinoptSdo}
                              setHsBinoptSdo={(v: boolean | undefined) => updateCurrentPatchField('hsBinoptSdo', v)}
                              hsBinoptUniaud={currentPatch.hsBinoptUniaud}
                              setHsBinoptUniaud={(v: boolean | undefined) => updateCurrentPatchField('hsBinoptUniaud', v)}
                              hsHomeName={currentPatch.hsHomeName || ''}
                              setHsHomeName={(v: string) => updateCurrentPatchField('hsHomeName', v)}
                              hsIgnoreOpatchConflict={currentPatch.hsIgnoreOpatchConflict ?? 'error'}
                              setHsIgnoreOpatchConflict={(v: 'error' | 'keep_first' | 'skip_all') => updateCurrentPatchField('hsIgnoreOpatchConflict', v)}
                              hsOsdbaGroup={currentPatch.hsOsdbaGroup || ''}
                              setHsOsdbaGroup={(v: string) => updateCurrentPatchField('hsOsdbaGroup', v)}
                              hsOsoperGroup={currentPatch.hsOsoperGroup || ''}
                              setHsOsoperGroup={(v: string) => updateCurrentPatchField('hsOsoperGroup', v)}
                              hsOsbackupdbaGroup={currentPatch.hsOsbackupdbaGroup || ''}
                              setHsOsbackupdbaGroup={(v: string) => updateCurrentPatchField('hsOsbackupdbaGroup', v)}
                              hsOsdgdbaGroup={currentPatch.hsOsdgdbaGroup || ''}
                              setHsOsdgdbaGroup={(v: string) => updateCurrentPatchField('hsOsdgdbaGroup', v)}
                              hsOskmdbaGroup={currentPatch.hsOskmdbaGroup || ''}
                              setHsOskmdbaGroup={(v: string) => updateCurrentPatchField('hsOskmdbaGroup', v)}
                              hsOsracdbaGroup={currentPatch.hsOsracdbaGroup || ''}
                              setHsOsracdbaGroup={(v: string) => updateCurrentPatchField('hsOsracdbaGroup', v)}
                              hsReadOnly={!!currentPatch.hsReadOnly}
                              setHsReadOnly={(v: boolean) => updateCurrentPatchField('hsReadOnly', v)}
                              hsRuApply={!!currentPatch.hsRuApply}
                              setHsRuApply={(v: boolean) => updateCurrentPatchField('hsRuApply', v)}
                              hsEnvTmp={currentPatch.hsEnvTmp || ''}
                              setHsEnvTmp={(v: string) => updateCurrentPatchField('hsEnvTmp', v)}
                            />
                          ) : currentSubTab === 2 && executionMode === 'download' ? (
                            <DownloadSettings
                              download={currentPatch.download}
                              setDownload={(v: 'NO' | 'YES') => updateCurrentPatchField('download', v)}
                              keystorePath={keystorePath}
                              setKeystorePath={setKeystorePath}
                              patchFolder={currentPatch.patchFolder}
                              platform={currentPatch.platform}
                              downloadLocked={executionMode === 'download'}
                              disabledByGoldImage={!!currentPatch.patchOptions.useGoldImageFile}
                            />
                          ) : currentSubTab === 2 ? (
                            <PatchContent
                              patchOptions={currentPatch.patchOptions}
                              setPatchOptions={updateCurrentPatchOptions}
                              patchFolder={currentPatch.patchFolder}
                              setPatchFolder={(v: string) => updateCurrentPatchField('patchFolder', v)}
                              patchFolderPlaceholder={
                                currentPatch.platform === 'WINDOWS.X64'
                                  ? 'C:\\Oracle\\AutoUpgrade\\Patches'
                                  : '/home/oracle/autoupgrade/patches'
                              }
                              executionMode={executionMode}
                              targetVersionEnabled={currentPatch.targetVersionEnabled}
                              targetVersion={currentPatch.targetVersion}
                              patchKey={activeDbTab}
                              platform={currentPatch.platform}
                              download={currentPatch.download}
                            />
                          ) : currentSubTab === 3 && ['analyze', 'fixups', 'deploy'].includes(executionMode) ? (
                            <DownloadSettings
                              download={currentPatch.download}
                              setDownload={(v: 'NO' | 'YES') => updateCurrentPatchField('download', v)}
                              keystorePath={keystorePath}
                              setKeystorePath={setKeystorePath}
                              patchFolder={currentPatch.patchFolder}
                              platform={currentPatch.platform}
                              downloadLocked={false}
                              disabledByGoldImage={!!currentPatch.patchOptions.useGoldImageFile}
                            />
                          ) : currentSubTab === 4 && ['analyze', 'fixups', 'deploy'].includes(executionMode) ? (
                            <AdditionalOptions
                              restoration={currentPatch.restoration}
                              setRestoration={(v: boolean) => updateCurrentPatchField('restoration', v)}
                              tdeEnabled={false}
                              setTdeEnabled={() => {}}
                              tdeKeystorePath={keystorePath}
                              setTdeKeystorePath={setKeystorePath}
                              showKeystore={false}
                              platform={currentPatch.platform}
                              sourceTnsAdminEnabled={!!currentPatch.sourceTnsAdminEnabled}
                              setSourceTnsAdminEnabled={(v: boolean) => updateCurrentPatchField('sourceTnsAdminEnabled', v)}
                              sourceTnsAdminDir={currentPatch.sourceTnsAdminDir || ''}
                              setSourceTnsAdminDir={(v: string) => updateCurrentPatchField('sourceTnsAdminDir', v)}
                              targetTnsAdminEnabled={!!currentPatch.targetTnsAdminEnabled}
                              setTargetTnsAdminEnabled={(v: boolean) => updateCurrentPatchField('targetTnsAdminEnabled', v)}
                              targetTnsAdminDir={currentPatch.targetTnsAdminDir || ''}
                              setTargetTnsAdminDir={(v: string) => updateCurrentPatchField('targetTnsAdminDir', v)}
                              startTimeType={currentPatch.startTimeType || 'now'}
                              setStartTimeType={(v: 'now' | 'delay' | 'datetime') => {
                                updateCurrentPatchField('startTimeType', v);
                              }}
                              startTimeValue={currentPatch.startTimeValue || ''}
                              setStartTimeValue={(v: string) => {
                                updateCurrentPatchField('startTimeValue', v);
                              }}
                              dropGrpAfterPatching={!!currentPatch.dropGrpAfterPatching}
                              setDropGrpAfterPatching={(v: boolean) => updateCurrentPatchField('dropGrpAfterPatching', v)}
                              delAfterUpgradePfileEnabled={!!currentPatch.delAfterUpgradePfileEnabled}
                              setDelAfterUpgradePfileEnabled={(v: boolean) => updateCurrentPatchField('delAfterUpgradePfileEnabled', v)}
                              delAfterUpgradePfile={currentPatch.delAfterUpgradePfile || ''}
                              setDelAfterUpgradePfile={(v: string) => updateCurrentPatchField('delAfterUpgradePfile', v)}
                              addDuringUpgradePfileEnabled={!!currentPatch.addDuringUpgradePfileEnabled}
                              setAddDuringUpgradePfileEnabled={(v: boolean) => updateCurrentPatchField('addDuringUpgradePfileEnabled', v)}
                              addDuringUpgradePfile={currentPatch.addDuringUpgradePfile || ''}
                              setAddDuringUpgradePfile={(v: string) => updateCurrentPatchField('addDuringUpgradePfile', v)}
                              binaryOptionRac={currentPatch.binaryOptionRac}
                              setBinaryOptionRac={(v: boolean | undefined) => updateCurrentPatchField('binaryOptionRac', v)}
                              clusterNodesEnabled={!!currentPatch.clusterNodesEnabled}
                              setClusterNodesEnabled={(v: boolean) => updateCurrentPatchField('clusterNodesEnabled', v)}
                              clusterNodes={currentPatch.clusterNodes || ''}
                              setClusterNodes={(v: string) => updateCurrentPatchField('clusterNodes', v)}
                              executionMode={executionMode}
                              racRolling={currentPatch.racRolling || 'AUTO'}
                              setRacRolling={(v: 'DISABLED' | 'REQUIRED' | 'FORCE' | 'AUTO') => updateCurrentPatchField('racRolling', v)}
                              drainTimeout={currentPatch.drainTimeout || ''}
                              setDrainTimeout={(v: string) => updateCurrentPatchField('drainTimeout', v)}
                            />
                          ) : null}
                        </Box>
                      </>
                    ) : (
                      <>
                        <UpgradeSection
                          logDir={currentPatch.logDir || ''}
                          setLogDir={(v: string) => updateCurrentPatchField('logDir', v)}
                          sid={currentPatch.sid}
                          setSid={(v: string) => updateCurrentPatchField('sid', v)}
                          sourceHome={currentPatch.sourceHome}
                          setSourceHome={(v: string) => updateCurrentPatchField('sourceHome', v)}
                          targetHome={currentPatch.targetHome}
                          setTargetHome={(v: string) => updateCurrentPatchField('targetHome', v)}
                          platform={currentPatch.platform}
                          setPlatform={(v: string) => updateCurrentPatchField('platform', v)}
                          patchKey={activeDbTab}
                          placeholders={{
                            sid: `DB${currentPatchIdx + 1}`,
                            source_home: BASIC_PLACEHOLDERS.source_home,
                            target_home: BASIC_PLACEHOLDERS.target_home,
                          }}
                          restoration={currentPatch.restoration}
                          setRestoration={(v: boolean) => updateCurrentPatchField('restoration', v)}
                          tdeEnabled={currentPatch.tdeEnabled}
                          setTdeEnabled={(v: boolean) => updateCurrentPatchField('tdeEnabled', v)}
                          tdeKeystorePath={keystorePath}
                          setTdeKeystorePath={setKeystorePath}
                          showKeystore={showTdeKeystore}
                          sourceTnsAdminEnabled={!!currentPatch.sourceTnsAdminEnabled}
                          setSourceTnsAdminEnabled={(v: boolean) => updateCurrentPatchField('sourceTnsAdminEnabled', v)}
                          sourceTnsAdminDir={currentPatch.sourceTnsAdminDir || ''}
                          setSourceTnsAdminDir={(v: string) => updateCurrentPatchField('sourceTnsAdminDir', v)}
                          targetTnsAdminEnabled={!!currentPatch.targetTnsAdminEnabled}
                          setTargetTnsAdminEnabled={(v: boolean) => updateCurrentPatchField('targetTnsAdminEnabled', v)}
                          targetTnsAdminDir={currentPatch.targetTnsAdminDir || ''}
                          setTargetTnsAdminDir={(v: string) => updateCurrentPatchField('targetTnsAdminDir', v)}
                          upgradeNodeEnabled={!!currentPatch.upgradeNodeEnabled}
                          setUpgradeNodeEnabled={(v: boolean) => updateCurrentPatchField('upgradeNodeEnabled', v)}
                          upgradeNodeValue={currentPatch.upgradeNodeValue || ''}
                          setUpgradeNodeValue={(v: string) => updateCurrentPatchField('upgradeNodeValue', v)}
                          upgradeType={currentPatch.upgradeType}
                          setUpgradeType={(v: string) => updateCurrentPatchField('upgradeType', v)}
                          distributedUpgrade={!!currentPatch.distributedUpgrade}
                          setDistributedUpgrade={(v: boolean) => updateCurrentPatchField('distributedUpgrade', v)}
                          targetCdbSid={currentPatch.targetCdbSid || ''}
                          setTargetCdbSid={(v: string) => updateCurrentPatchField('targetCdbSid', v)}
                          newPdbName={currentPatch.newPdbName || ''}
                          setNewPdbName={(v: string) => updateCurrentPatchField('newPdbName', v)}
                          fileNameConvertOption={currentPatch.fileNameConvertOption || 'convert'}
                          setFileNameConvertOption={(v: string) => updateCurrentPatchField('fileNameConvertOption', v)}
                          sourcePath={currentPatch.sourcePath || ''}
                          setSourcePath={(v: string) => updateCurrentPatchField('sourcePath', v)}
                          targetPath={currentPatch.targetPath || ''}
                          setTargetPath={(v: string) => updateCurrentPatchField('targetPath', v)}
                          sourcePdb={currentPatch.sourcePdb || ''}
                          setSourcePdb={(v: string) => updateCurrentPatchField('sourcePdb', v)}
                          targetCdb={currentPatch.targetCdbSid || ''}
                          setTargetCdb={(v: string) => updateCurrentPatchField('targetCdbSid', v)}
                          targetPdbName={currentPatch.targetPdbName || ''}
                          setTargetPdbName={(v: string) => updateCurrentPatchField('targetPdbName', v)}
                          dbLink={currentPatch.dbLink || ''}
                          setDbLink={(v: string) => updateCurrentPatchField('dbLink', v)}
                          refreshOption={currentPatch.refreshOption || ''}
                          setRefreshOption={(v: string) => updateCurrentPatchField('refreshOption', v)}
                          refreshInterval={currentPatch.refreshInterval || ''}
                          setRefreshInterval={(v: string) => updateCurrentPatchField('refreshInterval', v)}
                          parallelPdbCreation={currentPatch.parallelPdbCreation || ''}
                          setParallelPdbCreation={(v: string) => updateCurrentPatchField('parallelPdbCreation', v)}
                          dropDbLink={currentPatch.dropDbLink ?? false}
                          setDropDbLink={(v: boolean) => updateCurrentPatchField('dropDbLink', v)}
                          activeSubTab={currentSubTab}
                          setActiveSubTab={(v: number) => setActiveSubTabs(prev => ({ ...prev, [activeDbTab]: v }))}
                          startTimeType={currentPatch.startTimeType || 'now'}
                          setStartTimeType={(v: 'now' | 'delay' | 'datetime') => {
                            console.log('[Upgrade] setStartTimeType', v);
                            updateCurrentPatchField('startTimeType', v);
                          }}
                          startTimeValue={currentPatch.startTimeValue || ''}
                          setStartTimeValue={(v: string) => {
                            console.log('[Upgrade] setStartTimeValue', v);
                            updateCurrentPatchField('startTimeValue', v);
                          }}
                          winCredential={currentPatch.winCredential || ''}
                          setWinCredential={(v: string) => updateCurrentPatchField('winCredential', v)}
                          deleteWinCredentialFile={currentPatch.deleteWinCredentialFile !== false}
                          setDeleteWinCredentialFile={(v: boolean) => updateCurrentPatchField('deleteWinCredentialFile', v)}
                          dropGrpAfterUpgrade={!!currentPatch.dropGrpAfterUpgrade}
                          setDropGrpAfterUpgrade={(v: boolean) => updateCurrentPatchField('dropGrpAfterUpgrade', v)}
                          timezoneUpgUpg={!!currentPatch.timezoneUpgUpg}
                          setTimezoneUpgUpg={(v: boolean) => updateCurrentPatchField('timezoneUpgUpg', v)}
                          runUtlrpUpg={!!currentPatch.runUtlrpUpg}
                          setRunUtlrpUpg={(v: boolean) => updateCurrentPatchField('runUtlrpUpg', v)}
                          removeUnderscoreUpg={!!currentPatch.removeUnderscoreUpg}
                          setRemoveUnderscoreUpg={(v: boolean) => updateCurrentPatchField('removeUnderscoreUpg', v)}
                          raiseCompatibleUpg={!!currentPatch.raiseCompatibleUpg}
                          setRaiseCompatibleUpg={(v: boolean) => updateCurrentPatchField('raiseCompatibleUpg', v)}
                          raiseCompatibleUpgValue={currentPatch.raiseCompatibleUpgValue || ''}
                          setRaiseCompatibleUpgValue={(v: string) => updateCurrentPatchField('raiseCompatibleUpgValue', v)}
                          delDuringUpgradePfileEnabled={!!currentPatch.delDuringUpgradePfileEnabled}
                          setDelDuringUpgradePfileEnabled={(v: boolean) => updateCurrentPatchField('delDuringUpgradePfileEnabled', v)}
                          delDuringUpgradePfile={currentPatch.delDuringUpgradePfile || ''}
                          setDelDuringUpgradePfile={(v: string) => updateCurrentPatchField('delDuringUpgradePfile', v)}
                          addAfterUpgradePfileEnabled={!!currentPatch.addAfterUpgradePfileEnabled}
                          setAddAfterUpgradePfileEnabled={(v: boolean) => updateCurrentPatchField('addAfterUpgradePfileEnabled', v)}
                          addAfterUpgradePfile={currentPatch.addAfterUpgradePfile || ''}
                          setAddAfterUpgradePfile={(v: string) => updateCurrentPatchField('addAfterUpgradePfile', v)}
                          proactiveFixups={!!currentPatch.proactiveFixups}
                          setProactiveFixups={(v: boolean) => updateCurrentPatchField('proactiveFixups', v)}
                          delAfterUpgradePfile={currentPatch.delAfterUpgradePfile || ''}
                          setDelAfterUpgradePfile={(v: string) => updateCurrentPatchField('delAfterUpgradePfile', v)}
                          addDuringUpgradePfile={currentPatch.addDuringUpgradePfile || ''}
                          setAddDuringUpgradePfile={(v: string) => updateCurrentPatchField('addDuringUpgradePfile', v)}
                          delAfterUpgradePfileEnabled={!!currentPatch.delAfterUpgradePfileEnabled}
                          setDelAfterUpgradePfileEnabled={(v: boolean) => updateCurrentPatchField('delAfterUpgradePfileEnabled', v)}
                          addDuringUpgradePfileEnabled={!!currentPatch.addDuringUpgradePfileEnabled}
                          setAddDuringUpgradePfileEnabled={(v: boolean) => updateCurrentPatchField('addDuringUpgradePfileEnabled', v)}
                          operationType={operationType}
                          emTargetNameEnabled={!!currentPatch.emTargetNameEnabled}
                          setEmTargetNameEnabled={(v: boolean) => updateCurrentPatchField('emTargetNameEnabled', v)}
                          emTargetName={currentPatch.emTargetName || ''}
                          setEmTargetName={(v: string) => updateCurrentPatchField('emTargetName', v)}
                          emcliPathEnabled={!!currentPatch.emcliPathEnabled}
                          setEmcliPathEnabled={(v: boolean) => updateCurrentPatchField('emcliPathEnabled', v)}
                          emcliPath={currentPatch.emcliPath || ''}
                          setEmcliPath={(v: string) => updateCurrentPatchField('emcliPath', v)}
                          emBlackoutSuffixEnabled={!!currentPatch.emBlackoutSuffixEnabled}
                          setEmBlackoutSuffixEnabled={(v: boolean) => updateCurrentPatchField('emBlackoutSuffixEnabled', v)}
                          emBlackoutSuffix={currentPatch.emBlackoutSuffix || ''}
                          setEmBlackoutSuffix={(v: string) => updateCurrentPatchField('emBlackoutSuffix', v)}
                          manageStandbysClause={currentPatch.manageStandbysClause ?? ''}
                          setManageStandbysClause={(v: string) => updateCurrentPatchField('manageStandbysClause', v)}
                          updateCurrentPatchField={updateCurrentPatchField}
                        />
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>
            {/* Setup Cards: showBoth, showDownload, showTDE */}
            {showBoth && (
              <Paper elevation={6} sx={{ ...cardSx, ...sectionBorderSx, background: theme.palette.background.paper }}>
                <Box sx={{ px: 4, pt: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, ml: 0 }}>
                    <DescriptionOutlinedIcon sx={{ color: accentColor, fontSize: 24 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: accentColor, fontSize: '1rem' }}>
                      Initial Setup for Downloads and TDE Wallet
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 3, borderBottom: `2px solid ${accentColor}`, width: '100%' }} />
                </Box>
                <Box sx={{ px: 4, pb: 4 }}>
                  <Typography sx={{ mb: 2, fontSize: 18 }}>
                    You need to set up both MOS credentials for downloads and TDE wallet credentials:
                  </Typography>
                  <Paper sx={{ background: theme.palette.mode === 'light' ? '#f0f1f3' : '#2d3136', color: theme.palette.mode === 'light' ? '#23272f' : '#f8f8f2', p: 2, fontFamily: 'monospace', fontSize: 15, mb: 3, width: '100%', boxSizing: 'border-box', overflowX: 'auto', borderRadius: 2 }}>
                    {currentPatch.platform === 'WINDOWS.X64'
                      ? `java -jar autoupgrade.jar -config C:\\Oracle\\patch_${currentPatch.sid || BASIC_PLACEHOLDERS.sid}.cfg -patch -load_password`
                      : `java -jar autoupgrade.jar -config /home/oracle/patch_${currentPatch.sid || BASIC_PLACEHOLDERS.sid}.cfg -patch -load_password`}
                  </Paper>
                  <Paper sx={{ p: 2, border: `1.5px solid ${theme.palette.mode === 'light' ? '#1976d2' : '#2196f3'}`, background: theme.palette.mode === 'light' ? '#e3f2fd' : '#0d2236', color: theme.palette.mode === 'light' ? '#003366' : '#e0e0e0', display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ mr: 2, mt: 0.5 }}>
                      <Box sx={{ width: 36, height: 36, background: theme.palette.mode === 'light' ? '#1976d2' : '#2196f3', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>i</Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, mb: 1 }}>During this process:</Typography>
                      <ol style={{ margin: 0, paddingLeft: 18, fontSize: 16 }}>
                        <li>If the keystore doesn't exist, you'll be prompted to create it and provide a password</li>
                        <li>Type <b>group MOS</b> to be able to add the credentials for MOS</li>
                        <li>Type <b>add -user user@company.com</b> to add your MOS user credential when prompted</li>
                        <li>Enter your MOS password when prompted</li>
                        <li>Type <b>save</b> to save the current group configuration</li>
                        <li>Type <b>group TDE</b> to switch to TDE credentials</li>
                        <li>Type <b>add</b> to add the wallet password for your database</li>
                        <li>Enter the AutoUpgrade keystore password when prompted</li>
                        <li>Enter your secret/Password (your TDE wallet password) when prompted</li>
                        <li>Type <b>save</b> to save the configuration</li>
                        <li>Type <b>exit</b> to exit the load_password. If asked if you would like to make it as auto_login, you can choose to do it.</li>
                      </ol>
                    </Box>
                  </Paper>
                </Box>
              </Paper>
            )}
            {showDownload && !showBoth && (
              <Paper elevation={6} sx={{ ...cardSx, ...sectionBorderSx, background: theme.palette.background.paper }}>
                <Box sx={{ pt: 0, px: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, ml: 0, px: 0 }}>
                    <DescriptionOutlinedIcon sx={{ color: accentColor, fontSize: 24, verticalAlign: 'middle' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: accentColor, fontSize: '1rem', verticalAlign: 'middle' }}>
                      Initial Setup for Downloads
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2, borderBottom: `2px solid ${accentColor}`, width: '100%' }} />
                </Box>
                <Box sx={{ px: 0, pb: 2 }}>
                  <Typography sx={{ mb: 1.5, fontSize: 18 }}>
                    If you have configured <b>download=YES</b> in your configuration file, you need to first set up the keystore with the MOS credentials:
                  </Typography>
                  <Paper sx={{ background: theme.palette.mode === 'light' ? '#f0f1f3' : '#2d3136', color: theme.palette.mode === 'light' ? '#23272f' : '#f8f8f2', p: 2, fontFamily: 'monospace', fontSize: 15, mb: 3, width: '100%', boxSizing: 'border-box', overflowX: 'auto', borderRadius: 2 }}>
                    {currentPatch.platform === 'WINDOWS.X64'
                      ? `java -jar autoupgrade.jar -config C:\\Oracle\\patch_${currentPatch.sid || BASIC_PLACEHOLDERS.sid}.cfg -patch -load_password`
                      : `java -jar autoupgrade.jar -config /home/oracle/patch_${currentPatch.sid || BASIC_PLACEHOLDERS.sid}.cfg -patch -load_password`}
                  </Paper>
                  <Paper sx={{ mb: 2, p: 2, background: theme.palette.mode === 'light' ? '#fffbe6' : '#332900', border: `1.5px solid ${theme.palette.warning.main}`, color: theme.palette.warning.main, borderRadius: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 0.5 }}>Note:</Typography>
                    <Typography sx={{ fontSize: 15 }}>
                      <b>OCI accounts are not supported.</b> You must use a standard <b>My Oracle Support (MOS)</b> account (oracle.com login). OCI accounts cannot be used to download patches.
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, border: `1.5px solid ${theme.palette.mode === 'light' ? '#1976d2' : '#2196f3'}`, background: theme.palette.mode === 'light' ? '#e3f2fd' : '#0d2236', color: theme.palette.mode === 'light' ? '#003366' : '#e0e0e0', display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ mr: 2, mt: 0.5 }}>
                      <Box sx={{ width: 36, height: 36, background: theme.palette.mode === 'light' ? '#1976d2' : '#2196f3', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>i</Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, mb: 1 }}>During this process:</Typography>
                      <ol style={{ margin: 0, paddingLeft: 18, fontSize: 16 }}>
                        <li>If the keystore doesn't exist, you'll be prompted to create it and provide a password</li>
                        <li>Type <b>group MOS</b> to be able to add the credentials for MOS</li>
                        <li>Type <b>add -user user@company.com</b> to add your MOS user credential when prompted</li>
                        <li>Enter your MOS password when prompted</li>
                        <li>Type <b>save</b> to save the configuration</li>
                        <li>Type <b>exit</b> to exit the load_password. If asked if you would like to make it as auto_login, you can choose to do it.</li>
                      </ol>
                    </Box>
                  </Paper>
                </Box>
              </Paper>
            )}
            {showTDE && !showBoth && (
              <Paper elevation={6} sx={{ ...cardSx, ...sectionBorderSx, background: theme.palette.background.paper }}>
                <Box sx={{ pt: 0, px: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, ml: 0, px: 0 }}>
                    <DescriptionOutlinedIcon sx={{ color: accentColor, fontSize: 24, verticalAlign: 'middle' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: accentColor, fontSize: '1rem', verticalAlign: 'middle' }}>
                      Initial Setup for TDE Wallet
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2, borderBottom: `2px solid ${accentColor}`, width: '100%' }} />
                </Box>
                <Box sx={{ px: 0, pb: 2 }}>
                  <Typography sx={{ mb: 1.5, fontSize: 18 }}>
                    If you have configured <b>TDE</b> and the wallet type is not Auto-login, you need to first set up the keystore and TDE credentials:
                  </Typography>
                  <Paper sx={{ background: theme.palette.mode === 'light' ? '#f0f1f3' : '#2d3136', color: theme.palette.mode === 'light' ? '#23272f' : '#f8f8f2', p: 2, fontFamily: 'monospace', fontSize: 15, mb: 3, width: '100%', boxSizing: 'border-box', overflowX: 'auto', borderRadius: 2 }}>
                    {operationType === 'upgrade'
                      ? (currentPatch.platform === 'WINDOWS.X64'
                          ? `java -jar autoupgrade.jar -config C:\\Oracle\\AutoUpgrade\\upg_${currentPatch.sid || BASIC_PLACEHOLDERS.sid}.cfg -load_password`
                          : `java -jar autoupgrade.jar -config /home/oracle/upg_${currentPatch.sid || BASIC_PLACEHOLDERS.sid}.cfg -load_password`)
                      : (currentPatch.platform === 'WINDOWS.X64'
                          ? `java -jar autoupgrade.jar -config C:\\Oracle\\patch_${currentPatch.sid || BASIC_PLACEHOLDERS.sid}.cfg -load_password`
                          : `java -jar autoupgrade.jar -config /home/oracle/patch_${currentPatch.sid || BASIC_PLACEHOLDERS.sid}.cfg -load_password`)
                    }
                  </Paper>
                  <Paper sx={{ p: 2, border: `1.5px solid ${theme.palette.mode === 'light' ? '#1976d2' : '#2196f3'}`, background: theme.palette.mode === 'light' ? '#e3f2fd' : '#0d2236', color: theme.palette.mode === 'light' ? '#003366' : '#e0e0e0', display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ mr: 2, mt: 0.5 }}>
                      <Box sx={{ width: 36, height: 36, background: theme.palette.mode === 'light' ? '#1976d2' : '#2196f3', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>i</Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, mb: 1 }}>During this process:</Typography>
                      <ol style={{ margin: 0, paddingLeft: 18, fontSize: 16 }}>
                        <li>If the keystore doesn't exist, you'll be prompted to create it and provide a password</li>
                        <li>Type <b>group TDE</b> to be able to add the credentials for TDE</li>
                        <li>Type <b>add</b> to add the wallet password for your database</li>
                        <li>Enter the AutoUpgrade keystore password when prompted</li>
                        <li>Enter your secret/Password (your TDE wallet password) when prompted</li>
                        <li>Type <b>save</b> to save the configuration</li>
                        <li>Type <b>exit</b> to exit the load_password. If asked if you would like to make it as auto_login, you can choose to do it.</li>
                      </ol>
                    </Box>
                  </Paper>
                </Box>
              </Paper>
            )}
            {/* Windows Credentials Instructional Card - moved to left column, before Running AutoUpgrade */}
            {currentPatch.platform === 'WINDOWS.X64' && (
              <Paper elevation={6} sx={{ ...cardSx, ...sectionBorderSx, background: theme.palette.background.paper, borderRadius: 4, ml: 0, width: '100%' }}>
                <Box sx={{ pt: 0, px: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, ml: 0, px: 0 }}>
                    <DesktopWindowsIcon sx={{ color: accentColor, fontSize: 24 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: accentColor, fontSize: '1rem' }}>
                      Loading Windows Credentials
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 3, borderBottom: `2px solid ${accentColor}`, width: '100%' }} />
                </Box>
                <Box sx={{ px: 0, pb: 2 }}>
                  <Typography sx={{ mb: 2, fontSize: 18 }}>
                    If you are running on Windows, you need to set up the Windows Service credentials for patching:
                  </Typography>
                  <Box sx={{ mb: 2, width: '100%' }}>
                    <Box sx={{
                      fontFamily: 'monospace',
                      fontSize: 15,
                      background: theme.palette.mode === 'dark' ? '#2d3136' : '#f5f5f5',
                      color: theme.palette.mode === 'dark' ? '#f8f8f2' : '#23272f',
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                      display: 'block',
                      width: '100%',
                      overflowX: 'auto',
                      mb: 1.5,
                    }}>
                      {`java -jar autoupgrade.jar -config C:\\Oracle\\AutoUpgrade\\patch_${activeDbTab}.cfg -load_win_credential ${activeDbTab}`}
                    </Box>
                  </Box>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      border: `1.5px solid ${theme.palette.mode === 'light' ? '#1976d2' : '#2196f3'}`,
                      background: theme.palette.mode === 'light' ? '#e3f2fd' : '#0d2236',
                      color: theme.palette.mode === 'light' ? '#003366' : '#e0e0e0',
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: 2,
                    }}
                  >
                    <Box sx={{ mr: 2, mt: 0.5 }}>
                      <Box sx={{ width: 36, height: 36, background: theme.palette.mode === 'light' ? '#1976d2' : '#2196f3', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>i</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, mb: 1 }}>During this process:</Typography>
                      <ol style={{ margin: 0, paddingLeft: 18, fontSize: 16 }}>
                        <li>You will be prompted to input the credentials for the user running the Windows Service for the Oracle Database <b>{currentPatch.sid || BASIC_PLACEHOLDERS.sid}</b>.</li>
                        <li>Click on <b>OK</b>.</li>
                        <li>The credential file <b>C:\Oracle\AutoUpgrade\Credential\{currentPatch.sid || BASIC_PLACEHOLDERS.sid}.xml</b> will be created.</li>
                        <li>If you choose to delete the Windows Credentials, this file will be removed once the AutoUpgrade Patching operation is completed.</li>
                      </ol>
                    </Box>
                  </Paper>
                </Box>
              </Paper>
            )}
            {/* Patch Availability when Creating Home card - only for create_home mode */}
            {executionMode === 'create_home' && (
              <Paper elevation={2} sx={{ ...cardSx, ...sectionBorderSx }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <TableRowsOutlinedIcon sx={{ color: accentColor, fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: accentColor, fontSize: '1rem' }}>
                    Patch Availability when Creating Home
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3, borderBottomWidth: 2, width: '100%', borderColor: accentColor }} />
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <Paper
                    sx={{
                      p: 2.5,
                      border: `1.5px solid ${theme.palette.mode === 'light' ? '#1976d2' : '#2196f3'}`,
                      background: theme.palette.mode === 'light' ? '#e3f2fd' : '#0d2236',
                      color: theme.palette.mode === 'light' ? '#003366' : '#e0e0e0',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      borderRadius: 3,
                      width: '100%',
                      maxWidth: 1100,
                    }}
                  >
                    <Box sx={{ mr: 2, mt: 0.5 }}>
                      <Box sx={{ width: 36, height: 36, background: theme.palette.mode === 'light' ? '#1976d2' : '#2196f3', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>i</Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 16, color: 'inherit' }}>
                        The <b>create_home</b> mode does not download patches automatically.<br />
                        You must have previously called <b>-mode download</b> and have the patches on directory <b>{patches[0].patchFolder || (patches[0].platform === 'WINDOWS.X64' ? 'C:\\Oracle\\AutoUpgrade\\Patches' : '/home/oracle/autoupgrade/patches')}</b> along with the Oracle Database base image.
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              </Paper>
            )}
            {/* Running AutoUpgrade card */}
            <Paper elevation={6} sx={{ ...cardSx, ...sectionBorderSx, background: theme.palette.background.paper, borderRadius: 4, ml: 0, width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, ml: 0, pt: 0, px: 0 }}>
                <PlayArrowOutlinedIcon sx={{ color: accentColor, fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: accentColor, fontSize: '1rem' }}>
                  Running AutoUpgrade
                </Typography>
              </Box>
              <Box sx={{ px: 0 }}>
                <Box sx={{ mb: 3, borderBottom: `2px solid ${accentColor}`, width: '100%' }} />
                {executionMode === 'deploy' && (
                  <Paper sx={{ mb: 3, p: 2, background: theme.palette.mode === 'light' ? '#fffbe6' : '#332900', border: `1.5px solid ${theme.palette.warning.main}`, color: theme.palette.warning.main, borderRadius: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 0.5 }}>
                      Note:
                    </Typography>
                    <Typography sx={{ fontSize: 15 }}>
                      It is highly recommended to run AutoUpgrade with <b>-mode analyze</b> first to check for any potential issues before proceeding with a full deployment.
                    </Typography>
                  </Paper>
                )}
                {currentPatch.existingOracleHome && (
                  <Paper sx={{ mb: 3, p: 2, background: theme.palette.mode === 'light' ? '#e8f4fd' : '#0d2a3d', border: `1.5px solid ${theme.palette.info.main}`, color: theme.palette.info.main, borderRadius: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 0.5 }}>
                      Existing Target Home mode
                    </Typography>
                    <Typography sx={{ fontSize: 15 }}>
                      AutoUpgrade will <b>not</b> download or create a new Oracle Home. The DBA must ensure the Target Home already contains all desired patches (RU, OJVM, one-offs, etc.) before running this command. AutoUpgrade will move the database to the Target Home and run Datapatch to finalize the patching operation.
                    </Typography>
                  </Paper>
                )}
                <Typography sx={{ mb: 2, fontSize: 18 }}>
                  After setting up the keystore and credentials (if needed), you can run AutoUpgrade using:
                </Typography>
                <Paper
                  sx={{
                    background: theme.palette.mode === 'light' ? '#f8f9fa' : '#2d3136',
                    color: theme.palette.mode === 'light' ? '#222' : '#f8f8f2',
                    p: 2,
                    fontFamily: 'monospace',
                    fontSize: 15,
                    width: '100%',
                    boxSizing: 'border-box',
                    overflowX: 'auto',
                    borderRadius: 2,
                    my: 1,
                  }}
                >
                  {(() => {
                    const shouldIncludePatch = operationType === 'patch' && currentPatch.drainTimeout !== 'WAIT' && !currentPatch.existingOracleHome;
                    return currentPatch.platform === 'WINDOWS.X64'
                      ? `java -jar autoupgrade.jar -config C:\\Oracle\\AutoUpgrade\\${operationType === 'upgrade' ? `upg_${currentPatch.sid || BASIC_PLACEHOLDERS.sid}.cfg` : `patch_${currentPatch.sid || BASIC_PLACEHOLDERS.sid}.cfg`} ${shouldIncludePatch ? '-patch' : ''} -mode ${executionMode}`
                      : `java -jar autoupgrade.jar -config /home/oracle/${operationType === 'upgrade' ? `upg_${currentPatch.sid || BASIC_PLACEHOLDERS.sid}.cfg` : `patch_${currentPatch.sid || BASIC_PLACEHOLDERS.sid}.cfg`} ${shouldIncludePatch ? '-patch' : ''} -mode ${executionMode}`;
                  })()}
                </Paper>
              </Box>
            </Paper>
            {/* Move References card here, as last child in left column */}
            <Paper elevation={6} sx={{ ...cardSx, ...sectionBorderSx, background: theme.palette.background.paper, borderRadius: 4, ml: 0, width: '100%' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, ml: 0 }}>
                  <DescriptionOutlinedIcon sx={{ color: accentColor, fontSize: 24, verticalAlign: 'middle' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: accentColor, fontSize: '1rem', verticalAlign: 'middle' }}>
                    References
                  </Typography>
                </Box>
                <Box sx={{ mb: 3, borderBottom: `2px solid ${accentColor}`, width: '100%' }} />
              </Box>
              <Box sx={{ px: 0, pb: 2 }}>
                {/* Render <ReferencesURL /> content without its own header */}
                <ReferencesURL />
              </Box>
            </Paper>
          </Box>
          {/* Vertical Divider for columns - height matches left column */}
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', lg: 'block' }, mx: 0, borderColor: accentColor, borderRightWidth: 2, height: leftColHeight, position: 'absolute', left: '50%', top: 0, zIndex: 1 }} />
          {/* Right: Generated Config - sticky/fixed on large screens */}
          <Box
            sx={{
              flex: '0 1 46%',
              minWidth: 340,
              maxWidth: { xs: '100%', lg: '46%' },
              width: '100%',
              pl: { lg: '4%' },
              position: { xs: 'static', lg: 'sticky' },
              top: { lg: '64px' },
              alignSelf: 'flex-start',
              zIndex: 2,
              mt: { xs: 3, lg: 4 },
              pt: { xs: 5, lg: 6 }, // aumenta o padding-top do card invisível
            }}
          >
            <GeneratedConfig
              configText={configText}
              patches={patches}
              placeholders={{ sid: BASIC_PLACEHOLDERS.sid }}
              operationType={operationType as 'patch' | 'upgrade'}
              blink={blink}
              copied={copied}
              onCopy={() => {
                setCopied(true);
                setBlink(true);
                setTimeout(() => setCopied(false), 2000);
                setTimeout(() => setBlink(false), 1200);
              }}
              onConfigLoaded={handleConfigLoaded}
              invalid={isInvalidConfig}
            />
            {operationType === 'patch' && (executionMode === 'create_home' || executionMode === 'download' || executionMode === 'analyze' || executionMode === 'deploy' || executionMode === 'fixups') && (
              <ValidationMessages messages={validationMessages} />
            )}
            <Box sx={{ mt: 4 }}><AutoUpgradeReleaseCard /></Box>
          </Box>
        </Box>
      </Box>
      <Footer onChangelogClick={() => {
        setChangelogOpen(true);
        window.location.hash = '#changelog';
      }} />
      <Changelog open={changelogOpen} onClose={handleCloseChangelog} />

    </ThemeProvider>
  );
};

export default App;